import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fmtDateTime, fmtTime } from '../utils/dateUtils';
import { isRoomServiceEligible } from '../utils/roomServiceEligibility';
import ServiceTypeSelector from '../components/orders/ServiceTypeSelector';
import TableSelector from '../components/orders/TableSelector';
import RoomSelector from '../components/orders/RoomSelector';
import MenuAndOrder from '../components/orders/MenuAndOrder';

function Orders({ token, onOrdersUpdated, selectedOutlet, initialOrderId, onInitialOrderConsumed }) {
  const detectMobileUi = () => {
    if (typeof window === 'undefined') return false;
    const coarsePointer = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    const uaMobile = /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(window.navigator?.userAgent || '');
    return (coarsePointer || uaMobile) && window.innerWidth <= 1024;
  };
  const [isMobile, setIsMobile] = useState(() => detectMobileUi());
  const [mobileTab, setMobileTab] = useState('order'); // 'order' | 'cart' | 'history'
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [serviceTables, setServiceTables] = useState([]);
  const [tableCarts, setTableCarts] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [tableSearchTerm, setTableSearchTerm] = useState('');
  const [billingInfo, setBillingInfo] = useState({
    billing_name: '',
    billing_address: '',
    city: '',
    state: '',
    country: '',
    gst_number: ''
  });
  const [itemQuantities, setItemQuantities] = useState({});
  const [cartItems, setCartItems] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState('');
  const [activeTableSession, setActiveTableSession] = useState(null);
  const [allTableSessions, setAllTableSessions] = useState([]);
  const [tableSessionChecks, setTableSessionChecks] = useState([]);
  const [selectedCheckId, setSelectedCheckId] = useState('');
  const [blockingCheckCodes, setBlockingCheckCodes] = useState([]);
  const [sessionBusy, setSessionBusy] = useState(false);
  const [linkedTableInfo, setLinkedTableInfo] = useState(null); // { is_primary, is_secondary, linked_tables, primary_table }
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState('');
  const [error, setError] = useState('');
  const [billData, setBillData] = useState(null);
  const [billEditMode, setBillEditMode] = useState(false);
  const [itemBusy, setItemBusy] = useState(null); // item ID being modified
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [addOnOrderId, setAddOnOrderId] = useState(null);
  const [settlingOrderId, setSettlingOrderId] = useState(null);
  const [barSettlement, setBarSettlement] = useState({
    order_type: 'BAR',
    settlement_mode: 'DIRECT_PAYMENT',
    room_number: '',
    guest_name: '',
    mobile_last4: '',
    manager_approved: false,
    is_external_guest: false,
    customer_gstin: '',
    customer_name_bill: '',
    customer_address: ''
  });
  const [billPaymentType, setBillPaymentType] = useState('');
  const [propertyLogoUrl, setPropertyLogoUrl] = useState('');
  const [propertyConfig, setPropertyConfig] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [customerSessionFilter, setCustomerSessionFilter] = useState(null); // { id, mobile } | null
  const [settlingCustomerSid, setSettlingCustomerSid] = useState(null);
  const [sessionIndicator, setSessionIndicator] = useState('');

  // ── Step-based order flow ──
  const [step, setStep] = useState('service-type'); // 'service-type' | 'table' | 'room' | 'menu' | null
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [selectedTableObj, setSelectedTableObj] = useState(null);
  const [selectedRoomObj, setSelectedRoomObj] = useState(null);
  const [occupiedRooms, setOccupiedRooms] = useState([]);
  const [roomServiceOrders, setRoomServiceOrders] = useState([]);
  const [placingOrder, setPlacingOrder] = useState(false);
  const sessionIndicatorTimerRef = useRef(null);
  const chainId = localStorage.getItem('hotelConfigSelectedChainId') || '';
  const propertyId = localStorage.getItem('selectedPropertyId') || '';
  const authHeaders = (extra = {}) => {
    return {
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...extra
    };
  };
  const formatAmount = (value, currencyCode) => `${currencyCode || 'Rs.'} ${Number(value || 0).toFixed(2)}`;
  const isSettledBill = (row) => String(row?.bill_status || 'OPEN').toUpperCase() === 'SETTLED';
  const isVoidedBill = (row) => String(row?.bill_status || 'OPEN').toUpperCase() === 'VOID';
  const isOpenBill = (row) => String(row?.bill_status || 'OPEN').toUpperCase() === 'OPEN';
  const orderWorkflowStatus = (row) => String(row?.status || '').toUpperCase();
  const hasBillingDetails = (row) => Boolean(String(row?.payment_type || '').trim());
  const canSettleOrder = (row) => {
    if (!row) return false;
    if (isSettledBill(row) || isVoidedBill(row) || orderWorkflowStatus(row) === 'DRAFT') return false;
    const mode = String(barSettlement.settlement_mode || 'DIRECT_PAYMENT').toUpperCase();
    if (mode === 'DIRECT_PAYMENT') return hasBillingDetails(row);
    return true;
  };
  const tapMin = { minHeight: '44px' };

  useEffect(() => {
    const onResize = () => setIsMobile(detectMobileUi());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  useEffect(() => {
    loadOrders();
    loadMenuItems();
    loadServiceTables();
    loadCartDrafts();
    loadBillingInfo();
    loadPropertyLogo();
    loadAllTableSessions();
  }, [token, propertyId]);

  useEffect(() => {
    if (!propertyId) {
      setPropertyConfig(null);
      return;
    }
    fetch(`/api/v1/pms/property-config`, {
      headers: authHeaders()
    })
      .then((res) => res.json())
      .then((data) => {
        if (data) setPropertyConfig(data);
      })
      .catch(() => {
        setPropertyConfig(null);
      });
  }, [propertyId, token]);

  useEffect(() => {
    const timer = setInterval(() => {
      loadOrders();
      loadCartDrafts();
      loadAllTableSessions();
    }, 10000);
    return () => clearInterval(timer);
  }, [token, propertyId]);

  useEffect(() => () => {
    if (sessionIndicatorTimerRef.current) {
      clearTimeout(sessionIndicatorTimerRef.current);
      sessionIndicatorTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!selectedTableId) return;
    const selectedCart = tableCarts[selectedTableId] || { items: [], gst_id: '', payment_type: '' };
    setCartItems(Array.isArray(selectedCart.items) ? selectedCart.items : []);
    setSelectedPaymentType(selectedCart.payment_type || '');
  }, [selectedTableId, tableCarts]);

  useEffect(() => {
    if (!initialOrderId) return;
    openBillSmart(initialOrderId);
    onInitialOrderConsumed && onInitialOrderConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOrderId]);

  useEffect(() => {
    if (!selectedTableId) {
      setActiveTableSession(null);
      setTableSessionChecks([]);
      setSelectedCheckId('');
      setBlockingCheckCodes([]);
      return;
    }
    setActiveTableSession(null);
    setTableSessionChecks([]);
    setSelectedCheckId('');
    setBlockingCheckCodes([]);
    loadTableSession(selectedTableId);
  }, [selectedTableId]);

  const loadOrders = () => {
    fetch('/api/orders', {
      headers: authHeaders()
    })
      .then(res => {
        if (!res.ok) throw new Error(res.status === 403 ? 'Access denied — check permissions' : `Server error ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (Array.isArray(data)) setOrders(data);
        else if (data && Array.isArray(data.orders)) setOrders(data.orders);
        else setOrders([]);
      })
      .catch(err => {
        setError(err.message || 'Failed to load orders');
        setOrders([]);
      });
  };

  const loadMenuItems = () => {
    fetch('/api/menu-setup', {
      headers: authHeaders()
    })
      .then(res => res.json())
      .then(data => {
        const rows = Array.isArray(data) ? data : (data && Array.isArray(data.items) ? data.items : []);
        const initial = {};
        rows.forEach(item => {
          initial[item.id] = itemQuantities[item.id] || 1;
        });
        setItemQuantities(initial);
        setMenuItems(rows);
      })
      .catch(() => setMenuItems([]));
  };

  const loadServiceTables = () => {
    fetch('/api/table-master', {
      headers: authHeaders()
    })
      .then(res => res.json())
      .then(data => {
        const rows = Array.isArray(data) ? data : [];
        setServiceTables(rows.filter(row => row.is_active !== false));
      })
      .catch(() => setServiceTables([]));
  };

  const loadCartDrafts = () => {
    fetch('/api/orders/cart-drafts', {
      headers: authHeaders()
    })
      .then(res => res.json())
      .then(data => {
        const rows = Array.isArray(data) ? data : [];
        const mapped = {};
        rows.forEach((row) => {
          const tableId = String(row.table_id || '').trim();
          if (!tableId) return;
          mapped[tableId] = {
            items: Array.isArray(row.items) ? row.items : [],
            gst_id: row.gst_id || '',
            payment_type: row.payment_type || '',
            updated_by_username: row.updated_by_username || '',
            updated_at: row.updated_at || null
          };
        });
        setTableCarts(mapped);
      })
      .catch(() => setTableCarts({}));
  };

  const loadBillingInfo = () => {
    fetch('/api/bar-billing-info', { headers: authHeaders() })
      .then((res) => res.json())
      .then((data) => {
        setBillingInfo({
          billing_name: data?.billing_name || '',
          billing_address: data?.billing_address || '',
          city: data?.city || '',
          state: data?.state || '',
          country: data?.country || '',
          gst_number: data?.gst_number || ''
        });
      })
      .catch(() => {
        setBillingInfo({
          billing_name: '',
          billing_address: '',
          city: '',
          state: '',
          country: '',
          gst_number: ''
        });
      });
  };

  const loadPropertyLogo = () => {
    fetch('/api/properties/accessible', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.json())
      .then((data) => {
        const rows = Array.isArray(data) ? data : [];
        const selected = rows.find((p) => String(p.id) === String(propertyId));
        setPropertyLogoUrl(String(selected?.logo_url || '').trim());
      })
      .catch(() => setPropertyLogoUrl(''));
  };

  const loadAllTableSessions = async () => {
    if (!propertyId) {
      setAllTableSessions([]);
      return;
    }
    try {
      const res = await fetch('/api/table-sessions?status=OPEN', { headers: authHeaders() });
      if (!res.ok) {
        setAllTableSessions([]);
        return;
      }
      const data = await res.json();
      setAllTableSessions(Array.isArray(data) ? data : []);
    } catch {
      setAllTableSessions([]);
    }
  };

  const loadTableSession = async (tableId, preferredCheckCode = '') => {
    if (!tableId) {
      setActiveTableSession(null);
      setTableSessionChecks([]);
      setSelectedCheckId('');
      setLinkedTableInfo(null);
      setShowLinkPicker(false);
      return;
    }
    try {
      const sessionQs = new URLSearchParams({ table_id: String(tableId) });
      let res = await fetch(`/api/table-sessions/active?${sessionQs.toString()}`, { headers: authHeaders() });
      let data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setActiveTableSession(null);
        setTableSessionChecks([]);
        setSelectedCheckId('');
        return;
      }
      const session = data?.session || null;
      const checks = Array.isArray(data?.checks) ? data.checks : [];
      setActiveTableSession(session);
      setTableSessionChecks(checks);
      setLinkedTableInfo(data?.link_info || null);
      setShowLinkPicker(false);
      if (preferredCheckCode) {
        const preferred = checks.find((c) => String(c.check_code || '').toUpperCase() === String(preferredCheckCode || '').toUpperCase());
        if (preferred) {
          setSelectedCheckId(String(preferred.id));
          return;
        }
      }
      const existing = checks.find((c) => String(c.id) === String(selectedCheckId));
      if (existing) return;
      const openCheck = checks.find((c) => String(c.status || '').toUpperCase() === 'OPEN');
      setSelectedCheckId(openCheck ? String(openCheck.id) : '');
    } catch {
      setActiveTableSession(null);
      setTableSessionChecks([]);
      setSelectedCheckId('');
    }
  };

  const openSessionForSelectedTable = async () => {
    if (!selectedTableId) return setError('Select table first.');
    setSessionBusy(true);
    try {
      const res = await fetch('/api/table-sessions/open', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ table_id: Number(selectedTableId) })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to open session');
      setActiveTableSession(data?.session || null);
      const checks = Array.isArray(data?.checks) ? data.checks : [];
      setTableSessionChecks(checks);
      const openCheck = checks.find((c) => String(c.status || '').toUpperCase() === 'OPEN');
      setSelectedCheckId(openCheck ? String(openCheck.id) : '');
      setError('');
      // Refresh all table sessions to update occupancy indicators
      loadAllTableSessions();
    } catch {
      setError('Network error while opening session');
    } finally {
      setSessionBusy(false);
    }
  };

  const closeSessionForSelectedTable = async () => {
    if (!activeTableSession?.id) return setError('No active session to close.');
    const activeSessionTableId = String(activeTableSession?.table_id || '').trim();
    if (activeSessionTableId && String(selectedTableId || '').trim() && activeSessionTableId !== String(selectedTableId)) {
      return setError('Session changed. Reload table session and try again.');
    }
    const closeReason = String(window.prompt('Close session reason (optional)', '') || '').trim();
    if (!window.confirm('Clear (close) this session?')) return;
    setSessionBusy(true);
    try {
      const res = await fetch(`/api/table-sessions/${activeTableSession.id}/close`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ close_reason: closeReason || undefined })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const blockingCodes = Array.isArray(data?.blocking_check_codes) ? data.blocking_check_codes.filter(Boolean) : [];
        await loadTableSession(selectedTableId, blockingCodes[0] || '');
        setBlockingCheckCodes(blockingCodes);
        const details = blockingCodes.length ? ` Blocking checks: ${blockingCodes.join(', ')}` : '';
        return setError(`${data?.error || 'Failed to close session.'}${details}`);
      }
      console.log('Session closed:', data);
      setActiveTableSession(null);
      setTableSessionChecks([]);
      setSelectedCheckId('');
      setBlockingCheckCodes([]);
      setError('');
      // Refresh all table sessions to update occupancy indicators
      loadAllTableSessions();
      loadTableSession(selectedTableId);
    } catch {
      setError('Network error while closing session');
    } finally {
      setSessionBusy(false);
    }
  };

  const focusFirstBlockingCheck = async () => {
    if (!selectedTableId || !blockingCheckCodes.length) return;
    await loadTableSession(selectedTableId, blockingCheckCodes[0]);
  };

  const createNewCheck = async () => {
    if (!activeTableSession?.id) return setError('Open session first.');
    const nextCode = `C${(tableSessionChecks?.length || 0) + 1}`;
    setSessionBusy(true);
    try {
      const res = await fetch(`/api/table-sessions/${encodeURIComponent(activeTableSession.id)}/checks`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ check_code: nextCode })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to create check');
      await loadTableSession(selectedTableId);
      setSelectedCheckId(String(data.id || ''));
      setError('');
    } catch {
      setError('Network error while creating check');
    } finally {
      setSessionBusy(false);
    }
  };

  const persistTableCartDraft = async (tableId, items, gstId, paymentType) => {
    if (!tableId) return;
    const cleanItems = Array.isArray(items) ? items : [];
    const hasDraft = cleanItems.length > 0 || Boolean(gstId) || Boolean(paymentType);
    const payload = {
      items: cleanItems,
      gst_id: gstId || null,
      payment_type: paymentType || null
    };
    try {
      const res = await fetch(`/api/orders/cart-drafts/${encodeURIComponent(tableId)}`, {
        method: hasDraft ? 'PUT' : 'DELETE',
        headers: authHeaders(hasDraft ? { 'Content-Type': 'application/json' } : {}),
        body: hasDraft ? JSON.stringify(payload) : undefined
      });
      if (res.ok) loadCartDrafts();
    } catch {
      // no-op: local UI state is already updated, sync can recover on next polling load
    }
  };

  const filteredMenuItems = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return menuItems.filter(item =>
      (item.menu_item_code || '').toLowerCase().includes(q) ||
      (item.menu_item_name || '').toLowerCase().includes(q)
    );
  }, [menuItems, searchTerm]);

  const filteredServiceTables = useMemo(() => {
    const q = String(tableSearchTerm || '').trim().toLowerCase();
    if (!q) return serviceTables;
    return serviceTables.filter((tbl) => {
      const tableNo = String(tbl?.table_name || tbl?.table_no || '').toLowerCase();
      const zone = String(tbl?.zone || '').toLowerCase();
      return tableNo.includes(q) || zone.includes(q);
    });
  }, [serviceTables, tableSearchTerm]);

  const filteredOrders = useMemo(() => {
    let list = orders;
    if (customerSessionFilter?.id) {
      list = list.filter(o => String(o.customer_session_id || '') === String(customerSessionFilter.id));
    }
    if (statusFilter === 'ALL') return list;
    if (statusFilter === 'DRAFT') return list.filter(o => orderWorkflowStatus(o) === 'DRAFT');
    if (statusFilter === 'ACTIVE') return list.filter(o => orderWorkflowStatus(o) === 'SENT_TO_BAR' && isOpenBill(o));
    if (statusFilter === 'OPEN') return list.filter(o => isOpenBill(o));
    if (statusFilter === 'SETTLED') return list.filter(o => isSettledBill(o));
    if (statusFilter === 'VOID') return list.filter(o => isVoidedBill(o));
    return list;
  }, [orders, statusFilter, customerSessionFilter]);

  // Mask mobile for customer column display: "+91 ****6793".
  const maskMobile = (m) => {
    const s = String(m || '').trim();
    if (!s) return '';
    const last4 = s.slice(-4);
    const cc = s.startsWith('+') ? s.slice(0, s.length - 10) : '';
    return `${cc ? cc + ' ' : ''}****${last4}`;
  };

  // POST /api/orders/customer-session/:sid/settle — settle every OPEN order
  // for one customer session as a single combined bill.
  const settleCustomerSession = async (sid, mobile) => {
    if (!sid) return;
    const openCount = orders.filter(o =>
      String(o.customer_session_id || '') === String(sid) && isOpenBill(o) && orderWorkflowStatus(o) !== 'DRAFT'
    ).length;
    if (!openCount) { setError('No OPEN orders to settle for this customer'); return; }
    if (!window.confirm(`Settle all ${openCount} open order${openCount === 1 ? '' : 's'} for ${maskMobile(mobile)}?\nThis closes their session and stamps a shared settlement reference.`)) return;
    setSettlingCustomerSid(String(sid));
    try {
      const r = await fetch(`/api/orders/customer-session/${sid}/settle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ payment_type: billPaymentType || null }),
      });
      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Failed to settle customer session'); return; }
      setError('');
      setCustomerSessionFilter(null);
      await loadOrders();
    } catch { setError('Network error settling customer session'); }
    finally { setSettlingCustomerSid(null); }
  };

  const statusCounts = useMemo(() => ({
    ALL: orders.length,
    DRAFT: orders.filter(o => orderWorkflowStatus(o) === 'DRAFT').length,
    ACTIVE: orders.filter(o => orderWorkflowStatus(o) === 'SENT_TO_BAR' && isOpenBill(o)).length,
    OPEN: orders.filter(o => isOpenBill(o)).length,
    SETTLED: orders.filter(o => isSettledBill(o)).length,
    VOID: orders.filter(o => isVoidedBill(o)).length
  }), [orders]);

  const saveTableCart = (tableId, items, gstId, paymentType) => {
    if (addOnOrderId) return;
    if (!tableId) return;
    setTableCarts(prev => {
      const next = { ...prev };
      const cleanItems = Array.isArray(items) ? items : [];
      if (cleanItems.length === 0 && !gstId && !paymentType) {
        delete next[tableId];
      } else {
        next[tableId] = { items: cleanItems, gst_id: gstId || '', payment_type: paymentType || '' };
      }
      return next;
    });
    persistTableCartDraft(tableId, items, gstId, paymentType);
  };

  const updateCardQty = (menuItemId, delta) => {
    const current = itemQuantities[menuItemId] || 1;
    setItemQuantities({ ...itemQuantities, [menuItemId]: Math.max(1, current + delta) });
  };

  const addToCart = item => {
    if (!selectedTableId) {
      setError('Select table first to add items.');
      return;
    }
    if (!selectedCheckId) {
      setError('Select check first (open session + choose check).');
      return;
    }
    const qty = itemQuantities[item.id] || 1;
    const exists = cartItems.find(ci => ci.menu_item_id === item.id);
    let nextItems;
    if (exists) {
      nextItems = cartItems.map(ci => ci.menu_item_id === item.id ? { ...ci, quantity_servings: ci.quantity_servings + qty } : ci);
    } else {
      nextItems = [
        ...cartItems,
        {
          menu_item_id: item.id,
          menu_item_code: item.menu_item_code,
          menu_item_name: item.menu_item_name,
          serving_ml: Number(item.serving_ml || 0),
          unit_price: Number((item.effective_unit_price ?? item.per_unit_price) || 0),
          regular_price: Number(item.per_unit_price || 0),
          price_profile_type: item.active_price_profile_type || null,
          tax_id: Number(item.tax_id || 0) > 0 ? Number(item.tax_id) : null,
          tax_regime: String(item.tax_regime || 'NONE').toUpperCase(),
          gst_id: Number(item.gst_id || 0) > 0 ? Number(item.gst_id) : null,
          gst_percent: Number(item.gst_percent || 0),
          cgst_percent: Number(item.cgst_percent || 0),
          sgst_percent: Number(item.sgst_percent || 0),
          igst_percent: Number(item.igst_percent || 0),
          quantity_servings: qty
        }
      ];
    }
    setCartItems(nextItems);
    saveTableCart(selectedTableId, nextItems, '', selectedPaymentType);
    setError('');
  };

  const updateCartQty = (menuItemId, delta) => {
    const nextItems = cartItems.map(ci => {
      if (ci.menu_item_id !== menuItemId) return ci;
      return { ...ci, quantity_servings: Math.max(1, ci.quantity_servings + delta) };
    });
    setCartItems(nextItems);
    saveTableCart(selectedTableId, nextItems, '', selectedPaymentType);
  };

  const removeFromCart = menuItemId => {
    const nextItems = cartItems.filter(ci => ci.menu_item_id !== menuItemId);
    setCartItems(nextItems);
    saveTableCart(selectedTableId, nextItems, '', selectedPaymentType);
  };

  const subtotal = useMemo(() => {
    return cartItems.reduce((sum, ci) => sum + (ci.quantity_servings * ci.unit_price), 0);
  }, [cartItems]);

  const taxTotals = useMemo(() => {
    return cartItems.reduce((acc, ci) => {
      const lineAmount = Number(ci.quantity_servings || 0) * Number(ci.unit_price || 0);
      const regime = String(ci.tax_regime || '').toUpperCase();
      const cgst = Number(ci.cgst_percent || 0);
      const sgst = Number(ci.sgst_percent || 0);
      const igst = Number(ci.igst_percent || 0);
      const pct = Number(ci.gst_percent || 0);
      if (regime === 'GST') {
        acc.cgstAmount += (lineAmount * cgst) / 100;
        acc.sgstAmount += (lineAmount * sgst) / 100;
        acc.igstAmount += (lineAmount * igst) / 100;
      } else {
        acc.otherTaxAmount += (lineAmount * pct) / 100;
      }
      return acc;
    }, { cgstAmount: 0, sgstAmount: 0, igstAmount: 0, otherTaxAmount: 0 });
  }, [cartItems]);

  const tableServerOccupancy = useMemo(() => {
    const map = {};
    const now = new Date();
    const isToday = (dateValue) => {
      const d = new Date(dateValue);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
    };
    const activeOrderStatuses = new Set(['DRAFT', 'SENT_TO_BAR']);

    for (const order of orders) {
      const tableId = String(order?.table_id || '').trim();
      if (!tableId) continue;
      if (!isToday(order.order_date)) continue;
      const orderStatus = String(order?.status || '').toUpperCase();
      const billStatus = String(order?.bill_status || '').toUpperCase();
      if (!activeOrderStatuses.has(orderStatus)) continue;
      if (billStatus && billStatus !== 'OPEN') continue;

      const qty = Number(order.table_qty_total || 0);
      if (qty <= 0) continue;
      const time = new Date(order.order_date).getTime() || 0;
      const existing = map[tableId];
      if (!existing || time > existing.time) {
        map[tableId] = { qty, time };
      }
    }
    return map;
  }, [orders]);

  const formatUpdatedAt = (value) => {
    if (!value) return '';
    return fmtTime(value);
  };

  const taxAmount = useMemo(() => taxTotals.cgstAmount + taxTotals.sgstAmount + taxTotals.igstAmount + taxTotals.otherTaxAmount, [taxTotals]);
  const totalAmount = useMemo(() => subtotal + taxAmount, [subtotal, taxAmount]);

  const isOrderFromToday = orderDate => {
    const now = new Date();
    const d = new Date(orderDate);
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  };
  const canEditDraft = (row) => isOrderFromToday(row?.order_date) && isOpenBill(row) && orderWorkflowStatus(row) === 'DRAFT';
  const canSendDraft = (row) => isOpenBill(row) && orderWorkflowStatus(row) === 'DRAFT';
  const canAddMore = (row) => isOpenBill(row) && orderWorkflowStatus(row) === 'SENT_TO_BAR';
  const canModifyItems = (row) => isOpenBill(row) && ['DRAFT', 'SENT_TO_BAR', 'OPEN', 'READY', 'PARTIAL_KOT'].includes(orderWorkflowStatus(row));

  const refreshBillData = async (orderId) => {
    try {
      const res = await fetch(`/api/orders/${orderId}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setBillData(data);
      }
    } catch (_) {}
  };

  const updateItemQty = async (orderId, itemId, newQty) => {
    if (newQty < 1) return;
    setItemBusy(itemId);
    try {
      const res = await fetch(`/api/orders/${orderId}/items/${itemId}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ qty: newQty })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.error || 'Failed to update item');
        return;
      }
      await refreshBillData(orderId);
      if (typeof onOrdersUpdated === 'function') onOrdersUpdated();
    } catch (e) { setError(e.message); }
    finally { setItemBusy(null); }
  };

  const removeItem = async (orderId, itemId, itemName) => {
    if (!window.confirm(`Remove "${itemName}" from this order?`)) return;
    setItemBusy(itemId);
    try {
      const res = await fetch(`/api/orders/${orderId}/items/${itemId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.error || 'Failed to remove item');
        return;
      }
      await refreshBillData(orderId);
      if (typeof onOrdersUpdated === 'function') onOrdersUpdated();
    } catch (e) { setError(e.message); }
    finally { setItemBusy(null); }
  };

  const showSessionIndicator = (message) => {
    setSessionIndicator(String(message || ''));
    if (sessionIndicatorTimerRef.current) clearTimeout(sessionIndicatorTimerRef.current);
    sessionIndicatorTimerRef.current = setTimeout(() => {
      setSessionIndicator('');
      sessionIndicatorTimerRef.current = null;
    }, 2600);
  };

  const resetOrderForm = () => {
    setCartItems([]);
    setSelectedPaymentType('');
    setEditingOrderId(null);
    setAddOnOrderId(null);
    if (selectedTableId) saveTableCart(selectedTableId, [], '', '');
  };

  const clearSelectedTableDraft = async () => {
    if (!selectedTableId) {
      setError('Select table first.');
      return;
    }
    const confirmed = window.confirm('Clear draft for selected table?');
    if (!confirmed) return;
    try {
      const tableId = String(selectedTableId);
      const draftOrders = (orders || []).filter((o) =>
        String(o?.table_id || '') === tableId &&
        orderWorkflowStatus(o) === 'DRAFT'
      );

      for (const order of draftOrders) {
        const delRes = await fetch(`/api/orders/${order.id}`, {
          method: 'DELETE',
          headers: authHeaders()
        });
        if (!delRes.ok) {
          const errData = await delRes.json().catch(() => ({}));
          setError(errData.error || `Failed to clear draft order #${order.id}`);
          return;
        }
      }

      saveTableCart(selectedTableId, [], '', '');
      setCartItems([]);
      
      setSelectedPaymentType('');
      setEditingOrderId(null);
      setAddOnOrderId(null);
      setError('');
      loadOrders();
      if (onOrdersUpdated) onOrdersUpdated();
    } catch {
      setError('Network error while clearing selected table draft');
    }
  };

  const startEditOrder = async order => {
    if (isSettledBill(order)) {
      setError(`Bill #${order.id} is already settled and cannot be edited.`);
      return;
    }
    if (orderWorkflowStatus(order) !== 'DRAFT') {
      setError('Only DRAFT orders can be edited.');
      return;
    }
    if (!isOrderFromToday(order.order_date)) {
      setError('Only current day orders can be edited.');
      return;
    }

    try {
      const res = await fetch(`/api/orders/${order.id}`, {
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to load order for edit');
        return;
      }

      const editableCart = (data.items || [])
        .filter(item => Number(item.menu_item_id))
        .map(item => ({
          menu_item_id: Number(item.menu_item_id),
          menu_item_code: item.menu_item_code,
          menu_item_name: item.menu_item_name,
          serving_ml: Number(item.serving_ml || 0),
          unit_price: Number(item.unit_price || 0),
          tax_id: Number(item.tax_id || 0) > 0 ? Number(item.tax_id) : null,
          tax_regime: String(item.tax_regime || 'NONE').toUpperCase(),
          gst_id: Number(item.gst_id || 0) > 0 ? Number(item.gst_id) : null,
          gst_percent: Number(item.gst_percent || 0),
          cgst_percent: Number(item.cgst_percent || 0),
          sgst_percent: Number(item.sgst_percent || 0),
          igst_percent: Number(item.igst_percent || 0),
          quantity_servings: Number(item.qty_servings || 1)
        }));

      if (editableCart.length === 0) {
        setError('This order has no editable line items.');
        return;
      }

      setCartItems(editableCart);
      setSelectedPaymentType(String(data.payment_type || ''));
      const editTableId = String(data.table_id || '');
      setSelectedTableId(editTableId);
      const checkFromOrder = String(data?.items?.[0]?.check_id || '').trim();
      if (checkFromOrder) setSelectedCheckId(checkFromOrder);
      if (editTableId) saveTableCart(editTableId, editableCart, '', String(data.payment_type || ''));
      setEditingOrderId(order.id);
      setAddOnOrderId(null);
      setBillData(null);
      setError('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setError('Network error');
    }
  };

  const startAddOnOrder = async (order) => {
    if (isSettledBill(order)) {
      setError(`Bill #${order.id} is already settled and cannot accept add-on items.`);
      return;
    }
    if (orderWorkflowStatus(order) !== 'SENT_TO_BAR') {
      setError('Add-on is allowed only for SENT_TO_BAR orders.');
      return;
    }

    try {
      const res = await fetch(`/api/orders/${order.id}`, {
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to load order for add-on');
        return;
      }

      setCartItems([]);
      setSelectedPaymentType(String(data.payment_type || ''));
      setSelectedTableId(String(data.table_id || ''));
      const checkFromOrder = String(data?.items?.[0]?.check_id || '').trim();
      if (checkFromOrder) setSelectedCheckId(checkFromOrder);
      setEditingOrderId(null);
      setAddOnOrderId(order.id);
      setBillData(null);
      setError('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setError('Network error');
    }
  };

  const upsertDraftOrder = async () => {
    if (cartItems.length === 0) {
      setError('Add at least one item to cart.');
      return null;
    }
    if (!selectedTableId) {
      setError('Please select Table from Table Master.');
      return null;
    }
    if (!selectedCheckId) {
      setError('Please select Check before saving order.');
      return null;
    }

    const payload = {
      table_id: Number(selectedTableId),
      check_id: String(selectedCheckId),
      payment_type: selectedPaymentType || null,
      items: cartItems.map(ci => ({
        menu_item_id: ci.menu_item_id,
        quantity_servings: ci.quantity_servings
      }))
    };

    try {
      const isEditMode = Boolean(editingOrderId);
      const res = await fetch(isEditMode ? `/api/orders/${editingOrderId}` : '/api/orders', {
        method: isEditMode ? 'PUT' : 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || (isEditMode ? 'Draft update failed' : 'Draft create failed'));
        return null;
      }

      setError('');
      loadOrders();
      return data.id;
    } catch {
      setError('Network error');
      return null;
    }
  };

  const sendOrderToBar = async (orderId, { openBillAfter = false, autoPrintKot = false } = {}) => {
    if (!orderId) return false;
    try {
      const res = await fetch(`/api/orders/${orderId}/send-to-bar`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Failed to send order to BAR');
        return false;
      }
      setError('');
      loadOrders();
      if (onOrdersUpdated) onOrdersUpdated();
      localStorage.setItem('kot-raised-event', Date.now());
      if (openBillAfter) openBillSmart(orderId);
      if (autoPrintKot) {
        await printKotById(orderId);
      }
      return true;
    } catch {
      setError('Network error');
      return false;
    }
  };

  const saveDraft = async () => {
    const id = await upsertDraftOrder();
    if (!id) return;
    window.alert(`Draft saved as Order #${id}`);
  };

  const addItemsToExistingOrder = async (targetOrderId) => {
    if (!targetOrderId) return false;
    if (cartItems.length === 0) {
      setError('Add at least one item to cart.');
      return false;
    }
    if (!selectedCheckId) {
      setError('Select check before adding items.');
      return false;
    }
    try {
      const res = await fetch(`/api/orders/${targetOrderId}/add-items`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          check_id: selectedCheckId || null,
          items: cartItems.map(ci => ({
            menu_item_id: ci.menu_item_id,
            quantity_servings: ci.quantity_servings
          }))
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Failed to add items to existing order');
        return false;
      }
      setError('');
      loadOrders();
      if (onOrdersUpdated) onOrdersUpdated();
      await openBill(targetOrderId);
      await printKotById(targetOrderId);
      window.alert(`Added ${Number(data.added_items_count || 0)} item(s) to Order #${targetOrderId}`);
      resetOrderForm();
      return true;
    } catch {
      setError('Network error');
      return false;
    }
  };

  const findOpenOrderForSelectedCheck = async () => {
    if (!selectedCheckId || !selectedTableId) return null;
    const candidates = orders
      .filter((order) => (
        String(order?.table_id || '') === String(selectedTableId)
        && isOpenBill(order)
        && orderWorkflowStatus(order) !== 'DRAFT'
      ))
      .sort((a, b) => new Date(b.order_date || 0).getTime() - new Date(a.order_date || 0).getTime());

    for (const candidate of candidates) {
      try {
        const res = await fetch(`/api/orders/${candidate.id}`, { headers: authHeaders() });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) continue;
        const hasSameCheck = Array.isArray(data?.items) && data.items.some((item) => String(item?.check_id || '') === String(selectedCheckId));
        if (hasSameCheck) return Number(candidate.id);
      } catch {
      }
    }
    return null;
  };

  const placeOrderAndSendToBar = async () => {
    if (addOnOrderId) {
      await addItemsToExistingOrder(addOnOrderId);
      return;
    }

    const existingOrderId = await findOpenOrderForSelectedCheck();
    if (existingOrderId) {
      await addItemsToExistingOrder(existingOrderId);
      return;
    }

    const id = await upsertDraftOrder();
    if (!id) return;
    const ok = await sendOrderToBar(id, { openBillAfter: true, autoPrintKot: true });
    if (!ok) return;
    resetOrderForm();
  };

  const deleteOrder = async id => {
    const target = orders.find((o) => String(o.id) === String(id));
    if (target && orderWorkflowStatus(target) !== 'DRAFT') {
      setError(`Order #${id} is already sent to BAR and cannot be deleted.`);
      return;
    }
    if (target && !isOpenBill(target)) {
      setError(`Only OPEN bill can be deleted.`);
      return;
    }
    try {
      const res = await fetch(`/api/orders/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (res.ok) {
        loadOrders();
        if (onOrdersUpdated) onOrdersUpdated();
        setError('');
      } else {
        const data = await res.json();
        setError(data.error || 'Delete failed');
      }
    } catch {
      setError('Network error');
    }
  };

  const settleOrder = async (order) => {
    if (!order?.id) return;
    if (isSettledBill(order)) {
      setError(`Bill #${order.id} is already settled.`);
      return;
    }
    if (orderWorkflowStatus(order) === 'DRAFT') {
      setError(`Bill #${order.id} is still in DRAFT. Send to BAR before settlement.`);
      return;
    }
    const mode = String(barSettlement.settlement_mode || 'DIRECT_PAYMENT').toUpperCase();
    if (mode === 'DIRECT_PAYMENT' && !hasBillingDetails(order)) {
      setError(`Set payment type in Bill screen before settling Bill #${order.id}.`);
      return;
    }
    if (mode === 'PAY_AT_CHECKOUT') {
      const orderType = String(barSettlement.order_type || 'BAR').toUpperCase();
      if (!['IN_ROOM_DINING', 'ROOM_SERVICE'].includes(orderType)) return setError('Pay at Checkout is only available for IN_ROOM_DINING or ROOM_SERVICE orders');
      if (!barSettlement.is_external_guest && !String(barSettlement.room_number || '').trim()) return setError('Room number is required for Pay at Checkout');
      if (!String(barSettlement.guest_name || '').trim()) return setError('Guest name is required for Pay at Checkout');
      if (String(barSettlement.mobile_last4 || '').replace(/\D/g, '').length !== 4) return setError('Mobile last 4 digits are required');
    }
    const confirmed = window.confirm(`Settle Bill #${order.id}?`);
    if (!confirmed) return;
    setSettlingOrderId(String(order.id));
    try {
      const res = await fetch(`/api/orders/${order.id}/settle`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          settlement_mode: mode,
          order_type: String(barSettlement.order_type || 'BAR').toUpperCase(),
          room_number: barSettlement.room_number ? String(barSettlement.room_number).trim() : null,
          guest_name: barSettlement.guest_name ? String(barSettlement.guest_name).trim() : null,
          mobile_last4: barSettlement.mobile_last4 ? String(barSettlement.mobile_last4).replace(/\D/g, '').slice(-4) : null,
          is_external_guest: barSettlement.is_external_guest === true,
          manager_approved: barSettlement.manager_approved === true,
          customer_gstin: barSettlement.customer_gstin ? barSettlement.customer_gstin.trim().toUpperCase() : null,
          customer_name_bill: barSettlement.customer_name_bill ? barSettlement.customer_name_bill.trim() : null,
          customer_address: barSettlement.customer_address ? barSettlement.customer_address.trim() : null
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Failed to settle bill');
        return;
      }
      setError('');
      await loadOrders();
      if (billData && String(billData.id) === String(order.id)) {
        await openBill(order.id);
      }
      if (selectedTableId) {
        await loadTableSession(selectedTableId);
      }
      loadAllTableSessions();
      if (data?.auto_session_closed === true) {
        showSessionIndicator('Session auto-cleared');
      }
      window.alert(`Bill #${order.id} settled successfully.`);
    } catch {
      setError('Network error');
    } finally {
      setSettlingOrderId(null);
    }
  };

  const settleLinkedBill = async (primarySessionId, linkedTableNames) => {
    if (!billPaymentType) { setError('Select a payment type before settling the combined bill.'); return; }
    const tableList = (linkedTableNames || []).join(' + ');
    const confirmed = window.confirm(`Settle combined bill for ${tableList}?\nThis will settle ALL open orders and close sessions on all linked tables.`);
    if (!confirmed) return;
    setLinkBusy(true);
    try {
      const res = await fetch(`/api/table-sessions/${primarySessionId}/settle-linked`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ payment_type: billPaymentType })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to settle linked bill');
      setError('');
      // Update billData in-place with settlement ref + settled state so staff can print the receipt with the ref
      setBillData(prev => prev ? {
        ...prev,
        bill_status: 'SETTLED',
        settled_at: new Date().toISOString(),
        payment_type: billPaymentType,
        linked_settlement_id: data.linked_settlement_id || null
      } : null);
      await loadOrders();
      await loadTableSession(selectedTableId);
      loadAllTableSessions();
      showSessionIndicator('Linked bill settled — all tables cleared');
    } catch {
      setError('Network error settling linked bill');
    } finally {
      setLinkBusy(false);
    }
  };

  const linkTable = async (targetTableId) => {
    if (!activeTableSession?.id) return;
    setLinkBusy(true);
    try {
      const res = await fetch(`/api/table-sessions/${activeTableSession.id}/link`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ target_table_id: Number(targetTableId) })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to link table');
      setShowLinkPicker(false);
      setError('');
      await loadTableSession(selectedTableId);
    } catch {
      setError('Network error linking table');
    } finally {
      setLinkBusy(false);
    }
  };

  const unlinkTable = async (secondarySessionId) => {
    if (!activeTableSession?.id) return;
    setLinkBusy(true);
    try {
      const res = await fetch(`/api/table-sessions/${activeTableSession.id}/unlink-table`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ secondary_session_id: secondarySessionId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to unlink table');
      setError('');
      await loadTableSession(selectedTableId);
    } catch {
      setError('Network error unlinking table');
    } finally {
      setLinkBusy(false);
    }
  };

  const lookupBarInhouseGuest = async () => {
    const roomNo = String(barSettlement.room_number || '').trim();
    if (!roomNo) return setError('Enter room number first for lookup');
    try {
      const res = await fetch(`/api/bar/inhouse-guests?room_number=${encodeURIComponent(roomNo)}`, { headers: authHeaders() });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) return setError(data?.error || 'Failed to lookup in-house guest');
      const row = Array.isArray(data) ? data[0] : null;
      if (!row) {
        setBarSettlement((prev) => ({
          ...prev,
          guest_name: '',
          mobile_last4: ''
        }));
        return setError('No checked-in guest found for this room');
      }
      setBarSettlement((prev) => ({
        ...prev,
        guest_name: row.primary_guest_name || prev.guest_name,
        mobile_last4: row.mobile_last4 || prev.mobile_last4
      }));
      setError('');
    } catch {
      setError('Network error while looking up room guest');
    }
  };

  // ── Room Service: load occupied rooms ──
  const loadOccupiedRooms = async () => {
    if (!propertyId) return;
    try {
      const res = await fetch('/api/v1/pms/room-rack/live', { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) return;
      const rows = Array.isArray(data) ? data : (data?.rooms || []);
      // Only occupied, non-blocked rooms are serviceable. Shared rule (Bug 8720)
      // — previously this keyed off r.status (which room-rack/live doesn't return,
      // it returns occupancy_status) and never excluded blocked/out-of-order rooms.
      setOccupiedRooms(rows.filter(isRoomServiceEligible));
    } catch { /* silent */ }
  };

  const loadRoomServiceOrders = async () => {
    if (!propertyId) return;
    try {
      const res = await fetch('/api/orders', { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) return;
      const rows = (Array.isArray(data) ? data : (data?.orders || [])).filter(o => {
        const ot = String(o.order_type || '').toUpperCase();
        const bs = String(o.bill_status || '').toUpperCase();
        return (ot === 'ROOM_SERVICE' || ot === 'IN_ROOM_DINING') && bs !== 'VOID' && bs !== 'SETTLED';
      });
      setRoomServiceOrders(rows);
    } catch { /* silent */ }
  };

  // ── Step flow handlers ──
  const handleServiceTypeSelect = (type) => {
    setSelectedServiceType(type);
    if (type === 'restaurant') {
      setBarSettlement(prev => ({ ...prev, order_type: 'BAR', is_external_guest: true }));
      setStep('table');
    } else if (type === 'room-service') {
      setBarSettlement(prev => ({ ...prev, order_type: 'IN_ROOM_DINING', settlement_mode: 'PAY_AT_CHECKOUT', is_external_guest: false }));
      loadOccupiedRooms();
      loadRoomServiceOrders();
      setStep('room');
    } else if (type === 'takeaway') {
      setBarSettlement(prev => ({ ...prev, order_type: 'BAR', is_external_guest: true }));
      setSelectedTableId('');
      setSelectedTableObj(null);
      setStep('menu');
    }
  };

  const handleTableSelect = async (table) => {
    setSelectedTableObj(table);
    setSelectedTableId(String(table.id));
    setError('');
    // Auto-open session for this table
    await loadTableSession(String(table.id));
    // If no session exists, open one
    const sessionQs = new URLSearchParams({ table_id: String(table.id) });
    const sessionRes = await fetch(`/api/table-sessions/active?${sessionQs.toString()}`, { headers: authHeaders() }).catch(() => null);
    const sessionData = sessionRes ? await sessionRes.json().catch(() => ({})) : {};
    if (!sessionData?.session) {
      try {
        const openRes = await fetch('/api/table-sessions/open', {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ table_id: Number(table.id) })
        });
        const openData = await openRes.json().catch(() => ({}));
        if (openRes.ok) {
          setActiveTableSession(openData?.session || null);
          const checks = Array.isArray(openData?.checks) ? openData.checks : [];
          setTableSessionChecks(checks);
          const openCheck = checks.find(c => String(c.status || '').toUpperCase() === 'OPEN');
          setSelectedCheckId(openCheck ? String(openCheck.id) : '');
          loadAllTableSessions();
        }
      } catch { /* handled below */ }
    }
    setStep('menu');
  };

  const handleRoomSelect = (room) => {
    setSelectedRoomObj(room);
    setBarSettlement(prev => ({
      ...prev,
      room_number: String(room.room_number || ''),
      guest_name: room.guest_name || room.primary_guest_name || '',
      mobile_last4: room.mobile_last4 || '',
    }));
    setStep('menu');
  };

  const handleMenuPlaceOrder = async () => {
    setPlacingOrder(true);
    await placeOrderAndSendToBar();
    setPlacingOrder(false);
    if (!error) {
      if (selectedServiceType === 'restaurant') setStep('table');
      else if (selectedServiceType === 'room-service') { loadRoomServiceOrders(); setStep('room'); }
      else setStep('service-type');
    }
  };

  const handleMenuBack = () => {
    if (selectedServiceType === 'restaurant') setStep('table');
    else if (selectedServiceType === 'room-service') setStep('room');
    else setStep('service-type');
  };

  const handleMenuUpdateNote = (menuItemId, value) => {
    setCartItems(prev => prev.map(ci =>
      (ci.menu_item_id || ci.id) === menuItemId ? { ...ci, special_instructions: value } : ci
    ));
  };

  const voidOrder = async (order) => {
    if (!order?.id) return;
    if (isSettledBill(order)) {
      setError(`Bill #${order.id} is already settled and cannot be voided.`);
      return;
    }
    if (isVoidedBill(order)) {
      setError(`Bill #${order.id} is already voided.`);
      return;
    }
    const comments = window.prompt(`Enter void comments for Bill #${order.id}`);
    if (comments === null) return;
    const trimmed = String(comments || '').trim();
    if (!trimmed) {
      setError('Void comments are required.');
      return;
    }
    try {
      const res = await fetch(`/api/orders/${order.id}/void`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ comments: trimmed })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Failed to void bill');
        return;
      }
      setError('');
      await loadOrders();
      if (billData && String(billData.id) === String(order.id)) {
        await openBill(order.id);
      }
      if (selectedTableId) {
        await loadTableSession(selectedTableId);
      }
      loadAllTableSessions();
      if (onOrdersUpdated) onOrdersUpdated();
      if (data?.auto_session_closed === true) {
        showSessionIndicator('Session auto-cleared');
      }
      window.alert(`Bill #${order.id} voided successfully.`);
    } catch {
      setError('Network error');
    }
  };

  const fetchBillingInfo = async () => {
    try {
      const res = await fetch('/api/bar-billing-info', { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return null;
      return {
        billing_name: data?.billing_name || '',
        billing_address: data?.billing_address || '',
        city: data?.city || '',
        state: data?.state || '',
        country: data?.country || '',
        gst_number: data?.gst_number || ''
      };
    } catch {
      return null;
    }
  };

  const openLinkedBill = async (primarySessionId) => {
    try {
      const [linkedRes, billingRes] = await Promise.all([
        fetch(`/api/table-sessions/${primarySessionId}/linked-orders`, { headers: authHeaders() }),
        fetchBillingInfo()
      ]);
      const data = await linkedRes.json();
      if (!linkedRes.ok) return setError(data.error || 'Failed to load linked bill');
      const resolvedBilling = billingRes || billingInfo;
      setBillingInfo(resolvedBilling);
      setBillData({
        is_linked_bill: true,
        primary_session_id: primarySessionId,
        linked_tables: data.linked_tables || [],
        items: data.items || [],
        orders: data.orders || [],
        ...data.combined,
        currency_code: data.combined?.currency_code || 'INR',
        billing_info: resolvedBilling
      });
      setBillPaymentType('');
      setError('');
    } catch {
      setError('Network error loading linked bill');
    }
  };

  const openBill = async id => {
    try {
      const [orderRes, billingRes] = await Promise.all([
        fetch(`/api/orders/${id}`, {
          headers: authHeaders()
        }),
        fetchBillingInfo()
      ]);
      const data = await orderRes.json();
      if (orderRes.ok) {
        const resolvedBilling = billingRes || billingInfo;
        setBillingInfo(resolvedBilling);
        setBillData({ ...data, billing_info: resolvedBilling });
        setBillPaymentType(String(data?.payment_type || ''));
        setError('');
      } else {
        setError(data.error || 'Failed to load bill');
      }
    } catch {
      setError('Network error');
    }
  };

  // Smart bill opener: if the current table is part of a linked session, always open the combined bill
  const openBillSmart = (orderId) => {
    const primarySessId = linkedTableInfo?.is_primary
      ? activeTableSession?.id
      : (linkedTableInfo?.is_secondary ? linkedTableInfo.primary_session_id : null);
    if (primarySessId) {
      openLinkedBill(primarySessId);
    } else {
      openBill(orderId);
    }
  };

  const printBill = order => {
    const formatQty = (value) => {
      const n = Number(value || 0);
      if (!Number.isFinite(n)) return '0';
      if (Math.abs(n - Math.trunc(n)) < 0.000001) return String(Math.trunc(n));
      return n.toFixed(2);
    };
    const orderRegime = String(order.tax_regime || '').toUpperCase();
    const taxLinesHtml = orderRegime === 'GST' || Number(order.cgst_amount || 0) > 0 || Number(order.sgst_amount || 0) > 0 || Number(order.igst_amount || 0) > 0
      ? `
        <div class="right">CGST (${Number(order.cgst_percent || 0).toFixed(2)}%): ${(order.currency_code || 'Rs.')} ${Number(order.cgst_amount || 0).toFixed(2)}</div>
        <div class="right">SGST (${Number(order.sgst_percent || 0).toFixed(2)}%): ${(order.currency_code || 'Rs.')} ${Number(order.sgst_amount || 0).toFixed(2)}</div>
        <div class="right">IGST (${Number(order.igst_percent || 0).toFixed(2)}%): ${(order.currency_code || 'Rs.')} ${Number(order.igst_amount || 0).toFixed(2)}</div>
      `
      : `<div class="right">Tax (${Number(order.gst_percent || 0).toFixed(2)}%): ${(order.currency_code || 'Rs.')} ${Number(order.gst_amount || 0).toFixed(2)}</div>`;
    const resolvedBilling = order?.billing_info || billingInfo || {};
    const logoUrl = String(propertyLogoUrl || '').trim();
    const isLinkedBill = order.is_linked_bill === true;
    const linkedTablesStr = isLinkedBill ? (order.linked_tables || []).join(' + ') : '';
    const linkedOrderRefs = isLinkedBill ? (order.orders || []).map(o => `#${o.id}`).join(', ') : '';
    const settlementRef = order.linked_settlement_id ? String(order.linked_settlement_id).slice(0, 8).toUpperCase() : '';

    const rows = (order.items || []).map((item, i) => {
      const isHH = item.promo_meta?.promotion_type === 'HAPPY_HOUR';
      const isComp = item.line_type === 'COMP';
      const basePrice = Number(item.base_unit_price || 0);
      const effPrice = Number(item.unit_price || 0);
      let rateCell;
      if (isComp) {
        rateCell = basePrice > 0
          ? `<span style="text-decoration:line-through;color:#999;">${basePrice.toFixed(2)}</span> <b style="color:#166534;">FREE</b>`
          : `<b style="color:#166534;">FREE</b>`;
      } else if (isHH && basePrice > effPrice) {
        rateCell = `<span style="text-decoration:line-through;color:#999;">${basePrice.toFixed(2)}</span> ${effPrice.toFixed(2)}`;
      } else {
        rateCell = effPrice.toFixed(2);
      }
      const amtCell = isComp ? '<span style="color:#166534;">—</span>' : Number(item.line_amount || 0).toFixed(2);
      let badge = '';
      if (isHH) badge = ' <b style="font-size:9px;background:#fef3c7;color:#92400e;padding:0 2px;border-radius:2px;">HH</b>';
      if (isComp) badge = ' <b style="font-size:9px;background:#dcfce7;color:#166534;padding:0 2px;border-radius:2px;">FREE</b>';
      return `
      <tr>
        ${isLinkedBill ? `<td class="col-tbl" style="font-size:9px;color:#1d4ed8;">${item.source_table_name || '-'}</td>` : `<td class="col-sl">${i + 1}</td>`}
        <td class="col-item">${item.menu_item_name || '-'}${badge}${isComp && item.promo_meta?.rule_name ? `<br><span style="font-size:9px;color:#666;">BOGO: ${item.promo_meta.rule_name}</span>` : ''}</td>
        <td class="right col-qty">${formatQty(item.qty_servings)}</td>
        <td class="right col-rate">${rateCell}</td>
        <td class="right col-amt">${amtCell}</td>
      </tr>
    `;
    }).join('');

    const html = `
      <html>
      <head>
        <title>${isLinkedBill ? `Combined Bill (${linkedTablesStr})` : `Bill #${order.id}`}</title>
        <style>
          @page { size: 80mm auto; margin: 4mm; }
          body { font-family: monospace; width: 72mm; margin: 0 auto; }
          h2 { text-align:center; margin: 0 0 6px 0; font-size: 16px; }
          .line { border-top: 1px dashed #000; margin: 6px 0; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; table-layout: fixed; }
          th, td { padding: 2px 0; vertical-align: top; }
          .right { text-align: right; }
          .col-sl { width: 8%; }
          .col-tbl { width: 16%; word-break: break-word; }
          .col-item { width: ${isLinkedBill ? '36%' : '44%'}; word-break: break-word; }
          .col-qty { width: 14%; }
          .col-rate { width: 16%; }
          .col-amt { width: 18%; }
        </style>
      </head>
      <body>
        <div style="display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:6px;">
          <div style="width:58px;height:58px;overflow:hidden;">
            ${logoUrl ? `<img src="${logoUrl}" alt="Hotel Logo" style="max-width:58px;max-height:58px;object-fit:contain;" />` : ''}
          </div>
          <h2 style="flex:1;text-align:center;margin:0;">${isLinkedBill ? 'COMBINED BILL' : 'BAR BILL'}</h2>
          <div style="width:58px;"></div>
        </div>
        ${resolvedBilling.billing_name ? `<div style="text-align:center;font-weight:bold;">${resolvedBilling.billing_name}</div>` : ''}
        ${resolvedBilling.billing_address ? `<div style="text-align:center;">${resolvedBilling.billing_address}</div>` : ''}
        ${(resolvedBilling.city || resolvedBilling.state || resolvedBilling.country) ? `<div style="text-align:center;">${[resolvedBilling.city, resolvedBilling.state, resolvedBilling.country].filter(Boolean).join(', ')}</div>` : ''}
        ${resolvedBilling.gst_number ? `<div style="text-align:center;">Tax No: ${resolvedBilling.gst_number}</div>` : ''}
        ${isLinkedBill
          ? `<div>Tables: ${linkedTablesStr}</div>
             <div>Orders: ${linkedOrderRefs}</div>
             ${settlementRef ? `<div><b>Settlement Ref: ${settlementRef}</b></div>` : ''}`
          : `<div>Order: #${order.id}</div>`
        }
        <div>Status: ${String(order.bill_status || 'OPEN').toUpperCase()}</div>
        ${order.settled_at ? `<div>Settled At: ${fmtDateTime(order.settled_at)}</div>` : ''}
        ${order.voided_at ? `<div>Voided At: ${fmtDateTime(order.voided_at)}</div>` : ''}
        ${order.void_reason ? `<div>Void Comments: ${order.void_reason}</div>` : ''}
        <div>Date: ${fmtDateTime(order.order_date || Date.now())}</div>
        ${!isLinkedBill ? `<div>Table: ${order.table_name || '-'}</div>` : ''}
        <div>Payment: ${order.payment_type || billPaymentType || '-'}</div>
        <div>Currency: ${order.currency_code || '-'}</div>
        <div class="line"></div>
        <table>
          <thead><tr>
            ${isLinkedBill ? '<th style="font-size:9px;">Tbl</th>' : '<th>#</th>'}
            <th>Item</th><th class="right">Qty</th><th class="right">Rate</th><th class="right">Amt</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="line"></div>
        <div class="right">Order Amount: ${(order.currency_code || 'Rs.')} ${Number(order.subtotal || 0).toFixed(2)}</div>
        ${(() => {
          const hhSavings = (order.items || []).reduce((sum, it) => {
            if (it.promo_meta?.promotion_type !== 'HAPPY_HOUR') return sum;
            const saved = (Number(it.base_unit_price || 0) - Number(it.unit_price || 0)) * Number(it.qty_servings || 0);
            return sum + (saved > 0 ? saved : 0);
          }, 0);
          return hhSavings > 0
            ? `<div class="right" style="color:#92400e;">Happy Hour Savings: -${(order.currency_code || 'Rs.')} ${hhSavings.toFixed(2)}</div>`
            : '';
        })()}
        ${(() => {
          const bogoSavings = (order.items || []).reduce((sum, it) => {
            if (it.line_type !== 'COMP') return sum;
            return sum + Number(it.base_unit_price || 0) * Number(it.qty_servings || 0);
          }, 0);
          return bogoSavings > 0
            ? `<div class="right" style="color:#166534;">BOGO Savings (Free Items): -${(order.currency_code || 'Rs.')} ${bogoSavings.toFixed(2)}</div>`
            : '';
        })()}
        ${taxLinesHtml}
        <div class="right"><b>Total: ${(order.currency_code || 'Rs.')} ${Number(order.total_amount || 0).toFixed(2)}</b></div>
      </body>
      </html>
    `;

    const w = window.open('', '_blank', 'width=420,height=700');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const printKotSlip = (order) => {
    const formatQty = (value) => {
      const n = Number(value || 0);
      if (!Number.isFinite(n)) return '0';
      if (Math.abs(n - Math.trunc(n)) < 0.000001) return String(Math.trunc(n));
      return n.toFixed(2);
    };
    const logoUrl = String(propertyLogoUrl || '').trim();
    const rows = (order.items || []).map((item, i) => `
      <tr>
        <td class="col-sl">${i + 1}</td>
        <td class="col-item">${item.menu_item_name || '-'}</td>
        <td class="right col-serving">${Number(item.serving_ml || 0)} ml</td>
        <td class="right col-qty">${formatQty(item.qty_servings)}</td>
      </tr>
    `).join('');

    const html = `
      <html>
      <head>
        <title>KOT #${order.id}</title>
        <style>
          @page { size: 80mm auto; margin: 4mm; }
          body { font-family: monospace; width: 72mm; margin: 0 auto; }
          h2 { text-align:center; margin: 0 0 6px 0; font-size: 15px; }
          .line { border-top: 1px dashed #000; margin: 6px 0; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; table-layout: fixed; }
          th, td { padding: 2px 0; vertical-align: top; }
          .right { text-align: right; }
          .col-sl { width: 8%; }
          .col-item { width: 54%; word-break: break-word; }
          .col-serving { width: 20%; }
          .col-qty { width: 18%; font-weight: bold; }
        </style>
      </head>
      <body>
        <div style="display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:6px;">
          <div style="width:42px;height:42px;overflow:hidden;">
            ${logoUrl ? `<img src="${logoUrl}" alt="Hotel Logo" style="max-width:42px;max-height:42px;object-fit:contain;" />` : ''}
          </div>
          <h2 style="flex:1;text-align:center;margin:0;">BAR KOT</h2>
          <div style="width:42px;"></div>
        </div>
        <div><b>Order:</b> #${order.id}</div>
        <div><b>Date:</b> ${fmtDateTime(order.order_date)}</div>
        <div><b>Table:</b> ${order.table_name || '-'}</div>
        <div><b>Status:</b> ${String(order.status || '').toUpperCase()} / ${String(order.bill_status || '').toUpperCase()}</div>
        <div class="line"></div>
        <table>
          <thead><tr><th class="col-sl">#</th><th class="col-item">Item</th><th class="right col-serving">Serving</th><th class="right col-qty">Qty</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="line"></div>
        <div style="text-align:center;font-weight:bold;">PREPARE ITEMS AS PER QTY</div>
      </body>
      </html>
    `;

    const w = window.open('', '_blank', 'width=420,height=700');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const printOrderById = async id => {
    try {
      const [res, latestBilling] = await Promise.all([
        fetch(`/api/orders/${id}`, {
          headers: authHeaders()
        }),
        fetchBillingInfo()
      ]);
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to load bill');
        return;
      }
      const resolvedBilling = latestBilling || billingInfo;
      setBillingInfo(resolvedBilling);
      printBill({ ...data, billing_info: resolvedBilling });
    } catch {
      setError('Network error');
    }
  };

  const printKotById = async (id) => {
    try {
      const res = await fetch(`/api/orders/${id}`, {
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to load KOT');
        return;
      }
      printKotSlip(data);
    } catch {
      setError('Network error');
    }
  };

  const applyBillingDetails = async (order) => {
    if (!order?.id) return;
    if (isVoidedBill(order)) return setError(`Bill #${order.id} is voided. Billing details cannot be changed.`);
    if (!billPaymentType) return setError('Select payment type for billing');
    try {
      const res = await fetch(`/api/orders/${order.id}/billing-details`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          payment_type: billPaymentType
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Failed to apply billing details');
        return;
      }
      setError('');
      await openBill(order.id);
      loadOrders();
      if (onOrdersUpdated) onOrdersUpdated();
    } catch {
      setError('Network error');
    }
  };

  /* old implementation replaced below */
  /*
  const openBill = async id => {
    try {
      const res = await fetch(`/api/orders/${id}`, {
        headers: authHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        setBillData(data);
        setError('');
      } else {
        setError(data.error || 'Failed to load bill');
      }
    } catch {
      setError('Network error');
    }
  };

  const printBill = order => {
    const rows = (order.items || []).map((item, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${item.menu_item_name || '-'}</td>
        <td style="text-align:right;">${item.qty_servings}</td>
        <td style="text-align:right;">${Number(item.unit_price || 0).toFixed(2)}</td>
        <td style="text-align:right;">${Number(item.line_amount || 0).toFixed(2)}</td>
      </tr>
    `).join('');

    const html = `
      <html>
      <head>
        <title>Bill #${order.id}</title>
        <style>
          @page { size: 80mm auto; margin: 4mm; }
          body { font-family: monospace; width: 72mm; margin: 0 auto; }
          h2 { text-align:center; margin: 0 0 6px 0; font-size: 16px; }
          .line { border-top: 1px dashed #000; margin: 6px 0; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th, td { padding: 2px 0; vertical-align: top; }
          .right { text-align: right; }
        </style>
      </head>
      <body>
        <h2>BAR BILL</h2>
        ${billingInfo.billing_name ? `<div style="text-align:center;font-weight:bold;">${billingInfo.billing_name}</div>` : ''}
        ${billingInfo.billing_address ? `<div style="text-align:center;">${billingInfo.billing_address}</div>` : ''}
        ${(billingInfo.city || billingInfo.state || billingInfo.country) ? `<div style="text-align:center;">${[billingInfo.city, billingInfo.state, billingInfo.country].filter(Boolean).join(', ')}</div>` : ''}
        ${billingInfo.gst_number ? `<div style="text-align:center;">GST: ${billingInfo.gst_number}</div>` : ''}
        <div>Order: #${order.id}</div>
        <div>Date: ${fmtDateTime(order.order_date)}</div>
        <div>Table: ${order.table_name || '-'}</div>
        <div>Payment: ${order.payment_type || '-'}</div>
        <div>Currency: ${order.currency_code || '-'}</div>
        <div class="line"></div>
        <table>
          <thead><tr><th>#</th><th>Item</th><th class="right">Qty</th><th class="right">Rate</th><th class="right">Amt</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="line"></div>
        <div class="right">Order Amount: ${(order.currency_code || 'Rs.')} ${Number(order.subtotal || 0).toFixed(2)}</div>
        <div class="right">CGST (${Number(order.cgst_percent || 0).toFixed(2)}%): ${(order.currency_code || 'Rs.')} ${Number(order.cgst_amount || 0).toFixed(2)}</div>
        <div class="right">SGST (${Number(order.sgst_percent || 0).toFixed(2)}%): ${(order.currency_code || 'Rs.')} ${Number(order.sgst_amount || 0).toFixed(2)}</div>
        <div class="right">IGST (${Number(order.igst_percent || 0).toFixed(2)}%): ${(order.currency_code || 'Rs.')} ${Number(order.igst_amount || 0).toFixed(2)}</div>
        <div class="right">GST Total (${Number(order.gst_percent || 0).toFixed(2)}%): ${(order.currency_code || 'Rs.')} ${Number(order.gst_amount || 0).toFixed(2)}</div>
        <div class="right"><b>Total: ${(order.currency_code || 'Rs.')} ${Number(order.total_amount || 0).toFixed(2)}</b></div>
      </body>
      </html>
    `;

    const w = window.open('', '_blank', 'width=420,height=700');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const printOrderById = async id => {
    try {
      const res = await fetch(`/api/orders/${id}`, {
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to load bill');
        return;
      }
      printBill(data);
    } catch {
      setError('Network error');
    }
  };
  */

  /* ── Shared sub-components (inline helpers for table/session/menu/cart/orders) ── */
  const tableCardRow = (mobile) => {
    const items = filteredServiceTables;
    if (mobile) {
      return (
        <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '4px', WebkitOverflowScrolling: 'touch' }}>
          {serviceTables.length === 0 ? (
            <span style={{ color: 'var(--theme-text-muted)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>No tables configured</span>
          ) : items.length === 0 ? (
            <span style={{ color: 'var(--theme-text-muted)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>No tables match</span>
          ) : items.map(tbl => {
            const serverOcc = tableServerOccupancy[String(tbl.id)];
            const draftMeta = tableCarts[String(tbl.id)] || {};
            const draftCount = (draftMeta?.items || []).reduce((sum, l) => sum + Number(l?.quantity_servings || 0), 0);
            const itemCount = draftCount > 0 ? draftCount : Number(serverOcc?.qty || 0);
            const hasActiveSession = allTableSessions.some(s => String(s.table_id) === String(tbl.id) && String(s.status).toUpperCase() === 'OPEN');
            const occupied = itemCount > 0 || hasActiveSession;
            const selected = String(tbl.id) === String(selectedTableId || '');
            return (
              <button key={tbl.id} type="button"
                onClick={() => { setSelectedTableId(String(tbl.id)); setError(''); }}
                style={{ flexShrink: 0, border: selected ? '2.5px solid #3b82f6' : ('1.5px solid ' + (occupied ? '#fbbf24' : 'var(--theme-border)')), borderRadius: '12px', padding: '0.7rem 0.8rem', cursor: 'pointer', background: selected ? '#eff6ff' : (occupied ? '#fffbeb' : 'var(--theme-bg-light)'), display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem', minWidth: '76px', minHeight: '76px', justifyContent: 'center' }}
              >
                <span style={{ fontSize: '1.4rem' }}>{occupied ? '🔴' : '🟢'}</span>
                <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b', textAlign: 'center', lineHeight: 1.2 }}>{tbl.table_name}</span>
                {occupied && <span style={{ fontSize: '0.68rem', color: '#b45309', fontWeight: 600 }}>{itemCount} items</span>}
              </button>
            );
          })}
        </div>
      );
    }
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))', gap: '0.45rem', maxHeight: '220px', overflowY: 'auto' }}>
        {serviceTables.length === 0 ? (
          <span style={{ color: 'var(--theme-text-muted)', fontSize: '0.82rem', gridColumn: '1/-1' }}>No tables configured in Table Master</span>
        ) : items.length === 0 ? (
          <span style={{ color: 'var(--theme-text-muted)', fontSize: '0.82rem', gridColumn: '1/-1' }}>No tables match your search</span>
        ) : items.map(tbl => {
          const serverOcc = tableServerOccupancy[String(tbl.id)];
          const draftMeta = tableCarts[String(tbl.id)] || {};
          const draftCount = (draftMeta?.items || []).reduce((sum, l) => sum + Number(l?.quantity_servings || 0), 0);
          const itemCount = draftCount > 0 ? draftCount : Number(serverOcc?.qty || 0);
          const hasActiveSession = allTableSessions.some(s => String(s.table_id) === String(tbl.id) && String(s.status).toUpperCase() === 'OPEN');
          const occupied = itemCount > 0 || hasActiveSession;
          const selected = String(tbl.id) === String(selectedTableId || '');
          const metaUser = String(draftMeta.updated_by_username || '').trim();
          const metaTime = formatUpdatedAt(draftMeta.updated_at);
          return (
            <button key={tbl.id} type="button"
              onClick={() => { setSelectedTableId(String(tbl.id)); setError(''); }}
              style={{ border: selected ? '2px solid #3b82f6' : ('1.5px solid ' + (occupied ? '#fbbf24' : 'var(--theme-border)')), borderRadius: '10px', padding: '0.6rem 0.4rem', cursor: 'pointer', background: selected ? '#eff6ff' : (occupied ? '#fffbeb' : 'var(--theme-bg-light)'), color: '#1e293b', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.15rem', minHeight: '64px', transition: 'all 0.12s' }}
            >
              <span style={{ fontSize: '1.1rem' }}>{occupied ? '🔴' : '🟢'}</span>
              <span style={{ fontWeight: 600, fontSize: '0.78rem', lineHeight: 1.2, textAlign: 'center' }}>{tbl.table_name}</span>
              {occupied && <span style={{ fontSize: '0.65rem', color: '#b45309', fontWeight: 500 }}>{itemCount} items</span>}
              {(metaUser || metaTime) && <span style={{ fontSize: '0.6rem', color: 'var(--theme-text-muted)' }}>{metaUser}{metaTime ? ` ${metaTime}` : ''}</span>}
            </button>
          );
        })}
      </div>
    );
  };

  const sessionChecksBar = (mobile) => (
    (() => {
      const selectedCheck = tableSessionChecks.find((c) => String(c.id) === String(selectedCheckId));
      const selectedCheckCode = String(selectedCheck?.check_code || '').trim().toUpperCase();
      const orderIncludesCheckCode = (order, checkCode) => {
        if (!checkCode) return false;
        const codes = String(order?.check_codes || '')
          .split(',')
          .map((v) => String(v || '').trim().toUpperCase())
          .filter(Boolean);
        return codes.includes(checkCode);
      };
      const checkOrders = orders
        .filter((order) => (
          String(order?.table_id || '') === String(selectedTableId || '')
          && orderIncludesCheckCode(order, selectedCheckCode)
        ))
        .sort((a, b) => new Date(b.order_date || 0).getTime() - new Date(a.order_date || 0).getTime());
      const billTarget = checkOrders.find((order) => orderWorkflowStatus(order) !== 'DRAFT' && !isVoidedBill(order)) || null;
      const voidTarget = checkOrders.find((order) => !isSettledBill(order) && !isVoidedBill(order) && orderWorkflowStatus(order) !== 'DRAFT') || null;

      const isPrimary = linkedTableInfo?.is_primary === true;
      const isSecondary = linkedTableInfo?.is_secondary === true;
      const linkedTables = linkedTableInfo?.linked_tables || [];
      const canLink = activeTableSession?.id && !isSecondary;
      // Tables that can be linked: open sessions on other tables, not already linked
      const linkableTables = serviceTables.filter(t =>
        String(t.id) !== String(selectedTableId) &&
        allTableSessions.some(s => String(s.table_id) === String(t.id) && String(s.status || '').toUpperCase() === 'OPEN') &&
        !linkedTables.some(lt => String(lt.table_id) === String(t.id))
      );

      return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
      {/* Link status bar */}
      {(isPrimary || isSecondary) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '0.3rem 0.6rem' }}>
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1d4ed8' }}>🔗 Linked Bill</span>
          {isPrimary && linkedTables.map(lt => (
            <span key={lt.session_id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.72rem', background: '#dbeafe', color: '#1d4ed8', borderRadius: '999px', padding: '0.15rem 0.5rem', fontWeight: 600 }}>
              {lt.table_name}
              <button type="button" onClick={() => unlinkTable(lt.session_id)} disabled={linkBusy}
                style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem', padding: '0 0.1rem', lineHeight: 1 }}>×</button>
            </span>
          ))}
          {isSecondary && linkedTableInfo?.primary_table && (
            <span style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>Primary: <b>{linkedTableInfo.primary_table.table_name}</b></span>
          )}
          {isPrimary && (
            <button type="button" onClick={() => openLinkedBill(activeTableSession.id)}
              style={{ marginLeft: 'auto', padding: '0.2rem 0.65rem', border: 'none', borderRadius: '999px', background: '#1d4ed8', color: '#fff', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700 }}>
              Combined Bill
            </button>
          )}
        </div>
      )}

      {/* Link table picker */}
      {showLinkPicker && (
        <div style={{ background: 'var(--theme-bg-light)', border: '1.5px solid #bfdbfe', borderRadius: '8px', padding: '0.5rem 0.7rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)', fontWeight: 600 }}>Link to table:</span>
          {linkableTables.length === 0
            ? <span style={{ fontSize: '0.72rem', color: 'var(--theme-text-muted)' }}>No tables with open sessions available</span>
            : linkableTables.map(t => (
              <button key={t.id} type="button" disabled={linkBusy} onClick={() => linkTable(t.id)}
                style={{ padding: '0.2rem 0.65rem', border: '1.5px solid #3b82f6', borderRadius: '999px', background: '#fff', color: '#1d4ed8', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>
                {t.table_name}
              </button>
            ))
          }
          <button type="button" onClick={() => setShowLinkPicker(false)}
            style={{ marginLeft: 'auto', padding: '0.2rem 0.55rem', border: 'none', borderRadius: '999px', background: 'var(--theme-border)', color: 'var(--theme-text-secondary)', cursor: 'pointer', fontSize: '0.72rem' }}>
            Cancel
          </button>
        </div>
      )}

    <div style={{ display: 'flex', gap: mobile ? '0.5rem' : '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: '0.75rem', color: 'var(--theme-text-secondary)', background: 'var(--theme-border-light)', padding: '0.25rem 0.6rem', borderRadius: '999px', flexShrink: 0 }}>
        Session #{activeTableSession?.session_no || '—'} · {String(activeTableSession?.status || 'OPEN').toUpperCase()}
      </span>
      {/* Link Table button */}
      {canLink && !showLinkPicker && (
        <button type="button" onClick={() => setShowLinkPicker(true)}
          style={{ padding: mobile ? '0.38rem 0.75rem' : '0.22rem 0.55rem', border: '1.5px solid #bfdbfe', borderRadius: '999px', background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer', fontSize: mobile ? '0.82rem' : '0.72rem', fontWeight: 600, minHeight: mobile ? '38px' : 'auto' }}>
          🔗 Link Table
        </button>
      )}
      {tableSessionChecks.map(check => {
        const st = String(check?.status || '').toUpperCase();
        const sel = String(check?.id || '') === String(selectedCheckId || '');
        const dis = st !== 'OPEN';
        return (
          <button key={check.id} type="button" disabled={dis}
            onClick={() => setSelectedCheckId(String(check.id))}
            style={{ border: sel ? '2px solid #3b82f6' : '1.5px solid var(--theme-border)', borderRadius: '999px', padding: mobile ? '0.38rem 0.9rem' : '0.22rem 0.6rem', background: sel ? '#dbeafe' : '#fff', color: dis ? 'var(--theme-text-muted)' : (sel ? '#1d4ed8' : '#475569'), cursor: dis ? 'not-allowed' : 'pointer', fontSize: mobile ? '0.88rem' : '0.75rem', fontWeight: sel ? 700 : 400, minHeight: mobile ? '40px' : 'auto' }}
            title={check.guest_name || ''}
          >
            {check.check_code || 'CHECK'}
            {Number(check.total_amount || 0) > 0 && <span style={{ marginLeft: '0.3rem', opacity: 0.6 }}>· {formatAmount(check.total_amount || 0)}</span>}
          </button>
        );
      })}
      {selectedCheckId && (
        <>
          <span style={{ fontSize: '0.75rem', color: '#1f8f4d', fontWeight: 700 }}>
            ✓ {selectedCheck?.check_code || '—'}
          </span>
          <button
            type="button"
            onClick={() => billTarget && openBillSmart(billTarget.id)}
            disabled={!billTarget}
            style={{
              padding: mobile ? '0.42rem 0.9rem' : '0.26rem 0.62rem',
              border: 'none',
              borderRadius: '999px',
              background: billTarget ? '#dbeafe' : 'var(--theme-border)',
              color: billTarget ? '#1d4ed8' : 'var(--theme-text-muted)',
              cursor: billTarget ? 'pointer' : 'not-allowed',
              fontSize: mobile ? '0.82rem' : '0.72rem',
              fontWeight: 700,
              minHeight: mobile ? '38px' : 'auto'
            }}
          >
            Bill
          </button>
          <button
            type="button"
            onClick={() => voidTarget && voidOrder(voidTarget)}
            disabled={!voidTarget}
            style={{
              padding: mobile ? '0.42rem 0.9rem' : '0.26rem 0.62rem',
              border: 'none',
              borderRadius: '999px',
              background: voidTarget ? '#fee2e2' : 'var(--theme-border)',
              color: voidTarget ? '#b91c1c' : 'var(--theme-text-muted)',
              cursor: voidTarget ? 'pointer' : 'not-allowed',
              fontSize: mobile ? '0.82rem' : '0.72rem',
              fontWeight: 700,
              minHeight: mobile ? '38px' : 'auto'
            }}
          >
            Void
          </button>
        </>
      )}
    </div>
    </div>
      );
    })()
  );

  const menuGrid = (mobile) => (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700, fontSize: mobile ? '0.92rem' : '0.9rem', color: '#1e293b', whiteSpace: 'nowrap' }}>Menu Items</div>
        <input
          placeholder="Search item..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ flex: 1, maxWidth: mobile ? '100%' : '260px', padding: '0.5rem 0.7rem', borderRadius: '8px', border: '1px solid var(--theme-border)', fontSize: '16px', outline: 'none', minHeight: '44px' }}
        />
      </div>
      {filteredMenuItems.length === 0 ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--theme-text-muted)', fontSize: '0.88rem' }}>No menu items found</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(148px, 1fr))', gap: mobile ? '0.65rem' : '0.55rem' }}>
          {filteredMenuItems.map(item => (
            <div key={item.id} style={{ border: '1.5px solid var(--theme-border)', borderRadius: '12px', background: '#fff', padding: mobile ? '0.875rem' : '0.7rem 0.6rem', display: 'flex', flexDirection: 'column', gap: '0.4rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.25rem' }}>
                  <div style={{ fontWeight: 700, fontSize: mobile ? '0.95rem' : '0.85rem', lineHeight: 1.3, color: '#1e293b', flex: 1, minWidth: 0 }}>{item.menu_item_name}</div>
                  {item.active_price_profile_type === 'HAPPY_HOUR' && (
                    <span style={{ fontSize: '0.65rem', fontWeight: 800, background: '#fef3c7', color: '#b45309', border: '1px solid #fcd34d', borderRadius: '5px', padding: '0.1rem 0.35rem', letterSpacing: '0.04em', flexShrink: 0, marginTop: '0.05rem' }}>HH</span>
                  )}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-muted)', marginTop: '0.1rem' }}>{item.menu_item_code}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)', marginTop: '0.15rem' }}>{item.serving_ml || 0} ml</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem', marginTop: '0.2rem', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: mobile ? '1.05rem' : '0.95rem', fontWeight: 700, color: '#0f766e' }}>₹{Number((item.effective_unit_price ?? item.per_unit_price) || 0).toFixed(2)}</span>
                  {item.active_price_profile_type === 'HAPPY_HOUR' && Number(item.per_unit_price || 0) > Number(item.effective_unit_price || 0) && (
                    <span style={{ fontSize: '0.72rem', color: 'var(--theme-text-muted)', textDecoration: 'line-through' }}>₹{Number(item.per_unit_price).toFixed(2)}</span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.25rem' }}>
                <button type="button" onClick={() => updateCardQty(item.id, -1)} style={{ width: mobile ? '40px' : '30px', height: mobile ? '40px' : '30px', border: '1.5px solid var(--theme-border)', borderRadius: '8px', background: 'var(--theme-bg-light)', color: '#475569', cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: mobile ? '1.2rem' : '1rem', padding: 0, flexShrink: 0 }}>−</button>
                <span style={{ fontWeight: 700, fontSize: mobile ? '1rem' : '0.9rem', minWidth: '22px', textAlign: 'center', color: '#1e293b' }}>{itemQuantities[item.id] || 1}</span>
                <button type="button" onClick={() => updateCardQty(item.id, 1)} style={{ width: mobile ? '40px' : '30px', height: mobile ? '40px' : '30px', border: '1.5px solid var(--theme-border)', borderRadius: '8px', background: 'var(--theme-bg-light)', color: '#475569', cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: mobile ? '1.2rem' : '1rem', padding: 0, flexShrink: 0 }}>+</button>
              </div>
              <button type="button" onClick={() => addToCart(item)} style={{ padding: mobile ? '0.65rem' : '0.38rem', border: 'none', borderRadius: '8px', background: '#1a2151', color: '#fff', cursor: 'pointer', fontSize: mobile ? '0.9rem' : '0.78rem', fontWeight: 600, minHeight: mobile ? '44px' : '34px' }}>
                Add to Cart
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );

  const cartPanel = (mobile) => (
    <>
      {/* Cart header info */}
      <div style={{ background: '#1a2151', color: '#fff', borderRadius: '12px', padding: mobile ? '0.875rem 1rem' : '0.875rem 1rem' }}>
        <div style={{ fontWeight: 700, fontSize: mobile ? '0.95rem' : '0.9rem', marginBottom: '0.25rem' }}>
          {addOnOrderId ? `Add-on — Order #${addOnOrderId}` : editingOrderId ? `Editing Draft #${editingOrderId}` : 'Current Order'}
        </div>
        <div style={{ fontSize: '0.78rem', opacity: 0.75, display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <span>Table: {selectedTableId ? (serviceTables.find(t => String(t.id) === String(selectedTableId))?.table_name || '—') : '—'}</span>
          <span>Check: {selectedCheckId ? (tableSessionChecks.find(c => String(c.id) === String(selectedCheckId))?.check_code || '—') : '—'}</span>
        </div>
      </div>
      {/* Hint */}
      <div style={{ background: addOnOrderId ? '#fef3c7' : '#eff6ff', border: `1px solid ${addOnOrderId ? '#fbbf24' : '#bfdbfe'}`, borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: mobile ? '0.82rem' : '0.77rem', color: addOnOrderId ? '#92400e' : '#1e40af' }}>
        {addOnOrderId ? <>Add items for Order #{addOnOrderId}, then click <b>Add Items</b>.</> : <>Select table → open session → pick check → add items → place order.</>}
      </div>
      {/* Cart items */}
      <div style={{ background: '#fff', borderRadius: '12px', padding: '0.875rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b', marginBottom: '0.55rem', display: 'flex', justifyContent: 'space-between' }}>
          <span>Cart</span>
          <span style={{ color: 'var(--theme-text-muted)', fontWeight: 400, fontSize: '0.8rem' }}>{cartItems.length} item{cartItems.length !== 1 ? 's' : ''}</span>
        </div>
        {cartItems.length === 0 ? (
          <div style={{ padding: mobile ? '2rem' : '1.5rem', textAlign: 'center', color: 'var(--theme-text-muted)', fontSize: '0.88rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.4rem' }}>🛒</div>
            <div>Cart is empty</div>
            {mobile && <button type="button" onClick={() => setMobileTab('order')} style={{ marginTop: '0.75rem', padding: '0.6rem 1.25rem', border: 'none', borderRadius: '8px', background: '#1a2151', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', minHeight: '44px' }}>Browse Menu</button>}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: mobile ? '0.55rem' : '0.45rem', maxHeight: mobile ? 'none' : '280px', overflowY: mobile ? 'visible' : 'auto', paddingRight: '2px' }}>
            {cartItems.map(ci => (
              <div key={ci.menu_item_id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: mobile ? '0.65rem 0.5rem' : '0.45rem 0.4rem', borderRadius: '10px', background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border-light)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <div style={{ fontWeight: 600, fontSize: mobile ? '0.9rem' : '0.8rem', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ci.menu_item_name}</div>
                    {ci.price_profile_type === 'HAPPY_HOUR' && (
                      <span style={{ fontSize: '0.6rem', fontWeight: 800, background: '#fef3c7', color: '#b45309', border: '1px solid #fcd34d', borderRadius: '4px', padding: '0.05rem 0.28rem', letterSpacing: '0.04em', flexShrink: 0 }}>HH</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>
                    {ci.serving_ml} ml · ₹{Number(ci.unit_price).toFixed(2)}
                    {ci.price_profile_type === 'HAPPY_HOUR' && Number(ci.regular_price || 0) > Number(ci.unit_price || 0) && (
                      <span style={{ marginLeft: '0.3rem', textDecoration: 'line-through', color: 'var(--theme-text-muted)' }}>₹{Number(ci.regular_price).toFixed(2)}</span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: mobile ? '0.4rem' : '0.28rem', flexShrink: 0 }}>
                  <button onClick={() => updateCartQty(ci.menu_item_id, -1)} style={{ width: mobile ? '36px' : '26px', height: mobile ? '36px' : '26px', border: '1.5px solid var(--theme-border)', borderRadius: '7px', background: '#fff', color: '#475569', cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, fontSize: mobile ? '1.1rem' : '0.9rem' }}>−</button>
                  <span style={{ fontWeight: 700, fontSize: mobile ? '1rem' : '0.85rem', minWidth: '22px', textAlign: 'center' }}>{ci.quantity_servings}</span>
                  <button onClick={() => updateCartQty(ci.menu_item_id, 1)} style={{ width: mobile ? '36px' : '26px', height: mobile ? '36px' : '26px', border: '1.5px solid var(--theme-border)', borderRadius: '7px', background: '#fff', color: '#475569', cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, fontSize: mobile ? '1.1rem' : '0.9rem' }}>+</button>
                </div>
                <div style={{ fontWeight: 700, fontSize: mobile ? '0.95rem' : '0.82rem', color: '#0f766e', minWidth: '64px', textAlign: 'right', flexShrink: 0 }}>₹{(ci.quantity_servings * ci.unit_price).toFixed(2)}</div>
                <button onClick={() => removeFromCart(ci.menu_item_id)} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.25rem', fontSize: mobile ? '1.1rem' : '0.9rem', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '28px', minHeight: '28px' }}>✕</button>
              </div>
            ))}
            {/* Summary */}
            <div style={{ marginTop: '0.6rem', paddingTop: '0.6rem', borderTop: '1.5px dashed var(--theme-border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: mobile ? '0.88rem' : '0.78rem', color: 'var(--theme-text-secondary)', marginBottom: '0.2rem' }}><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
              {taxTotals.cgstAmount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: mobile ? '0.82rem' : '0.75rem', color: 'var(--theme-text-muted)', marginBottom: '0.15rem' }}><span>CGST</span><span>₹{taxTotals.cgstAmount.toFixed(2)}</span></div>}
              {taxTotals.sgstAmount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: mobile ? '0.82rem' : '0.75rem', color: 'var(--theme-text-muted)', marginBottom: '0.15rem' }}><span>SGST</span><span>₹{taxTotals.sgstAmount.toFixed(2)}</span></div>}
              {taxTotals.igstAmount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: mobile ? '0.82rem' : '0.75rem', color: 'var(--theme-text-muted)', marginBottom: '0.15rem' }}><span>IGST</span><span>₹{taxTotals.igstAmount.toFixed(2)}</span></div>}
              {taxTotals.otherTaxAmount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: mobile ? '0.82rem' : '0.75rem', color: 'var(--theme-text-muted)', marginBottom: '0.15rem' }}><span>Tax</span><span>₹{taxTotals.otherTaxAmount.toFixed(2)}</span></div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: mobile ? '1.1rem' : '1rem', color: '#1e293b', marginTop: '0.3rem', paddingTop: '0.3rem', borderTop: '2px solid var(--theme-border)' }}>
                <span>Total</span><span style={{ color: '#0f766e' }}>₹{totalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Action buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <button type="button" onClick={placeOrderAndSendToBar} style={{ padding: mobile ? '1rem' : '0.8rem', border: 'none', borderRadius: '12px', background: '#1a2151', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: mobile ? '1rem' : '0.9rem', minHeight: mobile ? '54px' : '48px' }}>
          {addOnOrderId ? `Add Items to Order #${addOnOrderId}` : editingOrderId ? `Place Order (Draft #${editingOrderId})` : '🚀 Place Order & Send to BAR'}
        </button>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" disabled={Boolean(addOnOrderId)} onClick={saveDraft} style={{ flex: 1, padding: mobile ? '0.75rem' : '0.6rem', border: '1.5px solid var(--theme-border)', borderRadius: '10px', background: addOnOrderId ? 'var(--theme-border-light)' : '#fff', color: addOnOrderId ? 'var(--theme-text-muted)' : '#475569', cursor: addOnOrderId ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: mobile ? '0.92rem' : '0.82rem', minHeight: mobile ? '48px' : '44px' }}>
            {editingOrderId ? `Update Draft` : 'Save Draft'}
          </button>
          <button type="button" onClick={clearSelectedTableDraft} disabled={!selectedTableId || Boolean(addOnOrderId)} style={{ flex: 1, padding: mobile ? '0.75rem' : '0.6rem', border: '1.5px solid #fee2e2', borderRadius: '10px', background: (selectedTableId && !addOnOrderId) ? '#fff5f5' : 'var(--theme-border-light)', color: (selectedTableId && !addOnOrderId) ? '#ef4444' : 'var(--theme-text-muted)', cursor: (selectedTableId && !addOnOrderId) ? 'pointer' : 'not-allowed', fontWeight: 600, fontSize: mobile ? '0.92rem' : '0.82rem', minHeight: mobile ? '48px' : '44px' }}>
            Clear Draft
          </button>
        </div>
        {editingOrderId && (
          <button type="button" onClick={resetOrderForm} style={{ padding: mobile ? '0.7rem' : '0.5rem', border: '1.5px solid var(--theme-border)', borderRadius: '10px', background: '#fff', color: 'var(--theme-text-secondary)', cursor: 'pointer', fontSize: mobile ? '0.9rem' : '0.8rem', minHeight: mobile ? '44px' : '40px' }}>Cancel Edit</button>
        )}
      </div>
    </>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--theme-border-light)', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ── Breadcrumb Header ── */}
      <div style={{ background: '#1a2151', color: '#fff', padding: '0.5rem 1.5rem', fontSize: '0.82rem' }}>
        <span style={{ opacity: 0.8 }}>BAR POS</span> <span style={{ margin: '0 0.35rem', opacity: 0.5 }}>/</span> <span style={{ fontWeight: 600 }}>Orders</span>
      </div>

      {/* ── Error Banner ── */}
      {error && (
        <div style={{ background: '#fef2f2', borderLeft: '4px solid #ef4444', color: '#991b1b', padding: '0.65rem 1rem', fontSize: '0.88rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            {error.includes('Blocking checks') && blockingCheckCodes.length > 0 ? (
              <button
                type="button"
                onClick={focusFirstBlockingCheck}
                style={{ background: '#991b1b', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.3rem 0.55rem', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600, minHeight: '30px' }}
              >
                Open Blocking Check
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => { setError(''); setBlockingCheckCodes([]); }}
              style={{ background: 'none', border: 'none', color: '#991b1b', cursor: 'pointer', fontSize: '1.3rem', lineHeight: 1, padding: '0 0.25rem', minWidth: '32px', minHeight: '32px' }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {sessionIndicator && (
        <div style={{ position: 'fixed', top: isMobile ? '72px' : '16px', right: '16px', zIndex: 1100, background: '#14532d', color: '#fff', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.82rem', fontWeight: 600, boxShadow: '0 4px 10px rgba(0,0,0,0.18)' }}>
          {sessionIndicator}
        </div>
      )}

      {/* ── Step: Service Type Selection ── */}
      {step === 'service-type' && (
        <div>
          <div style={{ padding: '0.75rem 1.5rem' }}>
            <span onClick={() => setStep(null)} style={{ cursor: 'pointer', color: '#555', fontSize: '0.9rem' }}>&larr; Back to Dashboard</span>
          </div>
          <ServiceTypeSelector onSelect={handleServiceTypeSelect} mode="bar" allowedTypes={selectedOutlet?.kot_order_types} />
        </div>
      )}

      {/* ── Step: Table Selection ── */}
      {step === 'table' && (
        <TableSelector
          tables={serviceTables}
          allTableSessions={allTableSessions}
          recentOrders={orders}
          onSelect={handleTableSelect}
          onBack={() => setStep('service-type')}
          isMobile={isMobile}
        />
      )}

      {/* ── Step: Room Selection ── */}
      {step === 'room' && (
        <RoomSelector
          rooms={occupiedRooms}
          roomOrders={roomServiceOrders}
          onSelect={handleRoomSelect}
          onBack={() => setStep('service-type')}
          isMobile={isMobile}
        />
      )}

      {/* ── Step: Menu & Order ── */}
      {step === 'menu' && (
        <MenuAndOrder
          menuItems={menuItems}
          cartItems={cartItems}
          itemQuantities={itemQuantities}
          tableInfo={selectedTableObj}
          roomInfo={selectedRoomObj}
          serviceType={selectedServiceType}
          onAddToCart={addToCart}
          onUpdateCartQty={updateCartQty}
          onRemoveFromCart={removeFromCart}
          onUpdateNote={handleMenuUpdateNote}
          onUpdateCardQty={updateCardQty}
          onPlaceOrder={handleMenuPlaceOrder}
          onBack={handleMenuBack}
          isMobile={isMobile}
          placing={placingOrder}
          cartSubtotal={subtotal}
          cartTaxAmount={taxAmount}
          cartTotal={totalAmount}
        />
      )}

      {/* ══════════════ DESKTOP LAYOUT (legacy — shown when no step active) ══════════════ */}
      {!step && !isMobile && (
        <>
          {/* Action bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1.5rem' }}>
            <h2 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', color: '#1e293b' }}>Bar Orders</h2>
            <button onClick={() => setStep('service-type')} style={{ padding: '0.45rem 1rem', border: 'none', borderRadius: '8px', background: '#1a2151', color: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>+ New Order</button>
          </div>
          {/* Two-panel */}
          <div style={{ display: 'flex', minHeight: 'calc(100vh - 110px)' }}>

            {/* LEFT — Tables + Session + Menu */}
            <div style={{ flex: '0 0 62%', overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>

              {/* Table Grid */}
              <div style={{ background: '#fff', borderRadius: '12px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>Tables</div>
                  <input value={tableSearchTerm} onChange={e => setTableSearchTerm(e.target.value)} placeholder="Search table / zone..."
                    style={{ padding: '0.38rem 0.65rem', border: '1px solid var(--theme-border)', borderRadius: '8px', fontSize: '0.82rem', width: '190px', outline: 'none' }} />
                </div>
                {tableCardRow(false)}
              </div>

              {/* Session + Check */}
              {selectedTableId && (
                <div style={{ background: '#fff', borderRadius: '12px', padding: '0.875rem 1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      Session — {serviceTables.find(t => String(t.id) === String(selectedTableId))?.table_name || 'Table'}
                      {activeTableSession?.id
                        ? <span style={{ fontSize: '0.72rem', fontWeight: 700, background: '#dcfce7', color: '#15803d', borderRadius: '999px', padding: '0.15rem 0.55rem', letterSpacing: '0.03em' }}>● OPEN</span>
                        : <span style={{ fontSize: '0.72rem', fontWeight: 600, background: 'var(--theme-border-light)', color: 'var(--theme-text-muted)', borderRadius: '999px', padding: '0.15rem 0.55rem' }}>No Session</span>
                      }
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button type="button" onClick={openSessionForSelectedTable} disabled={!selectedTableId || Boolean(activeTableSession?.id) || sessionBusy}
                        style={{ padding: '0.4rem 0.8rem', border: 'none', borderRadius: '8px', background: (!selectedTableId || Boolean(activeTableSession?.id) || sessionBusy) ? 'var(--theme-border)' : '#10b981', color: (!selectedTableId || Boolean(activeTableSession?.id) || sessionBusy) ? 'var(--theme-text-muted)' : '#fff', cursor: (!selectedTableId || Boolean(activeTableSession?.id) || sessionBusy) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.8rem', minHeight: '36px' }}>
                        Open Session
                      </button>
                      <button type="button" onClick={createNewCheck} disabled={!selectedTableId || !activeTableSession?.id || sessionBusy}
                        style={{ padding: '0.4rem 0.8rem', border: 'none', borderRadius: '8px', background: (!selectedTableId || !activeTableSession?.id || sessionBusy) ? 'var(--theme-border)' : '#3b82f6', color: (!selectedTableId || !activeTableSession?.id || sessionBusy) ? 'var(--theme-text-muted)' : '#fff', cursor: (!selectedTableId || !activeTableSession?.id || sessionBusy) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.8rem', minHeight: '36px' }}>
                        + New Check
                      </button>
                      <button type="button" onClick={closeSessionForSelectedTable} disabled={!activeTableSession?.id || sessionBusy}
                        style={{ padding: '0.4rem 0.8rem', border: 'none', borderRadius: '8px', background: (!activeTableSession?.id || sessionBusy) ? 'var(--theme-border)' : '#ef4444', color: (!activeTableSession?.id || sessionBusy) ? 'var(--theme-text-muted)' : '#fff', cursor: (!activeTableSession?.id || sessionBusy) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.8rem', minHeight: '36px' }}>
                        Clear Session
                      </button>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--theme-text-secondary)', marginBottom: '0.55rem' }}>
                    Open Session creates C1. Use New Check only for split customer billing (C2, C3...).
                  </div>
                  {activeTableSession?.id
                    ? sessionChecksBar(false)
                    : <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-muted)', fontStyle: 'italic' }}>No active session — open one to start taking orders</div>
                  }
                </div>
              )}

              {/* Menu */}
              <div style={{ background: '#fff', borderRadius: '12px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', flex: 1 }}>
                <div style={{ maxHeight: '380px', overflowY: 'auto', paddingRight: '2px' }}>
                  {menuGrid(false)}
                </div>
              </div>
            </div>

            {/* RIGHT — Cart */}
            <div style={{ flex: '0 0 38%', overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', background: 'var(--theme-bg-light)', borderLeft: '1.5px solid var(--theme-border)' }}>
              {cartPanel(false)}
            </div>
          </div>

          {/* Desktop: Recent Orders */}
          <div style={{ padding: '1.25rem 1.5rem', background: '#fff', borderTop: '1.5px solid var(--theme-border)' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b', marginBottom: '0.875rem' }}>Recent Orders</div>
            
            {/* Status Filter Cards */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.875rem', flexWrap: 'wrap' }}>
              {[
                { key: 'ALL', label: 'All Orders', color: 'var(--theme-text-secondary)', bg: 'var(--theme-border-light)' },
                { key: 'DRAFT', label: 'Draft', color: '#6b21a8', bg: '#f3e8ff' },
                { key: 'ACTIVE', label: 'Active', color: '#1d4ed8', bg: '#dbeafe' },
                { key: 'OPEN', label: 'Open', color: '#c2410c', bg: '#fff7ed' },
                { key: 'SETTLED', label: 'Settled', color: '#15803d', bg: '#dcfce7' },
                { key: 'VOID', label: 'Void', color: '#b91c1c', bg: '#fee2e2' }
              ].map(status => {
                const isSelected = statusFilter === status.key;
                return (
                  <button
                    key={status.key}
                    type="button"
                    onClick={() => setStatusFilter(status.key)}
                    style={{
                      padding: '0.5rem 1rem',
                      border: isSelected ? `2px solid ${status.color}` : '1.5px solid var(--theme-border)',
                      borderRadius: '10px',
                      background: isSelected ? status.bg : '#fff',
                      color: isSelected ? status.color : 'var(--theme-text-secondary)',
                      cursor: 'pointer',
                      fontWeight: isSelected ? 700 : 500,
                      fontSize: '0.82rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      transition: 'all 0.15s',
                      minHeight: '36px'
                    }}
                  >
                    <span>{status.label}</span>
                    <span style={{ padding: '0.1rem 0.45rem', borderRadius: '999px', background: isSelected ? status.color : 'var(--theme-border)', color: isSelected ? '#fff' : 'var(--theme-text-secondary)', fontSize: '0.72rem', fontWeight: 700, minWidth: '22px', textAlign: 'center' }}>
                      {statusCounts[status.key]}
                    </span>
                  </button>
                );
              })}
            </div>

            {customerSessionFilter && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 0.9rem', marginBottom: '0.75rem', background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: '10px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.85rem', color: '#1e3a8a' }}>
                  Filtered to customer <b style={{ fontFamily: 'monospace' }}>{maskMobile(customerSessionFilter.mobile)}</b> · {filteredOrders.length} order{filteredOrders.length === 1 ? '' : 's'}
                </span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
                  <button type="button" onClick={() => settleCustomerSession(customerSessionFilter.id, customerSessionFilter.mobile)}
                    disabled={settlingCustomerSid === String(customerSessionFilter.id)}
                    style={{ padding: '0.4rem 0.85rem', border: 'none', borderRadius: '8px', background: '#15803d', color: '#fff', cursor: settlingCustomerSid === String(customerSessionFilter.id) ? 'wait' : 'pointer', fontSize: '0.82rem', fontWeight: 700 }}>
                    {settlingCustomerSid === String(customerSessionFilter.id) ? 'Settling…' : '💳 Settle Customer'}
                  </button>
                  <button type="button" onClick={() => setCustomerSessionFilter(null)}
                    style={{ padding: '0.4rem 0.85rem', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff', color: '#475569', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                    Clear filter
                  </button>
                </div>
              </div>
            )}
            {!filteredOrders || filteredOrders.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--theme-text-muted)', fontSize: '0.88rem', background: 'var(--theme-bg-light)', borderRadius: '10px', border: '1.5px dashed var(--theme-border)' }}>No orders found</div>
            ) : (
              <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid var(--theme-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <table style={{ width: '100%', minWidth: '880px', borderCollapse: 'collapse', background: '#fff' }}>
                  <thead>
                    <tr style={{ background: '#1a2151', color: '#fff' }}>
                      {['ORDER #','DATE & TIME','TABLE','CUSTOMER','CHECK','PAYMENT','STATUS','SUBTOTAL','TOTAL','ACTIONS'].map((h, i) => (
                        <th key={h} style={{ padding: '0.65rem 0.875rem', textAlign: i >= 7 && i <= 8 ? 'right' : (i === 9 ? 'center' : 'left'), fontWeight: 600, fontSize: '0.77rem', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order, idx) => (
                      <tr key={order.id} style={{ borderBottom: '1px solid var(--theme-border-light)', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ padding: '0.6rem 0.875rem', fontWeight: 700, color: '#1e293b', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>#{order.id}</td>
                        <td style={{ padding: '0.6rem 0.875rem', color: 'var(--theme-text-secondary)', fontSize: '0.79rem', whiteSpace: 'nowrap' }}>
                          <div>{fmtDateTime(order.order_date)}</div>
                          {order.settled_at && <div style={{ fontSize: '0.7rem', color: '#15803d' }}>Settled: {fmtTime(order.settled_at)}</div>}
                          {order.voided_at && <div style={{ fontSize: '0.7rem', color: '#b91c1c' }}>Voided: {fmtTime(order.voided_at)}</div>}
                        </td>
                        <td style={{ padding: '0.6rem 0.875rem', color: '#475569', fontSize: '0.82rem', fontWeight: 500 }}>{order.table_name || '—'}</td>
                        <td style={{ padding: '0.6rem 0.875rem', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                          {order.customer_session_id && order.customer_mobile ? (
                            <button type="button" onClick={() => setCustomerSessionFilter({ id: order.customer_session_id, mobile: order.customer_mobile })}
                              title="Filter to this customer's orders" style={{ border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', padding: '0.18rem 0.5rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'monospace' }}>
                              📱 {maskMobile(order.customer_mobile)}
                            </button>
                          ) : <span style={{ color: 'var(--theme-text-muted)' }}>—</span>}
                        </td>
                        <td style={{ padding: '0.6rem 0.875rem', color: '#0f766e', fontSize: '0.79rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{String(order.check_with_guest || order.check_codes || '').trim() || '—'}</td>
                        <td style={{ padding: '0.6rem 0.875rem', color: 'var(--theme-text-secondary)', fontSize: '0.79rem' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                            <span>{order.payment_type || '—'}</span>
                            {order.linked_settlement_id && (
                              <span title={`Combined settlement ref: ${order.linked_settlement_id}`} style={{ fontSize: '0.65rem', fontWeight: 700, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '4px', padding: '0.05rem 0.3rem', whiteSpace: 'nowrap' }}>
                                🔗 {String(order.linked_settlement_id).slice(0, 8).toUpperCase()}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '0.6rem 0.875rem' }}>
                          <div style={{ display: 'flex', gap: '0.28rem', flexWrap: 'wrap' }}>
                            <span style={{ padding: '0.16rem 0.48rem', borderRadius: '999px', fontSize: '0.68rem', fontWeight: 600, background: orderWorkflowStatus(order) === 'DRAFT' ? '#f3e8ff' : '#dbeafe', color: orderWorkflowStatus(order) === 'DRAFT' ? '#6b21a8' : '#1d4ed8' }}>{orderWorkflowStatus(order) || 'DRAFT'}</span>
                            <span style={{ padding: '0.16rem 0.48rem', borderRadius: '999px', fontSize: '0.68rem', fontWeight: 600, background: isSettledBill(order) ? '#dcfce7' : (isVoidedBill(order) ? '#fee2e2' : '#fff7ed'), color: isSettledBill(order) ? '#15803d' : (isVoidedBill(order) ? '#b91c1c' : '#c2410c') }}>{String(order.bill_status || 'OPEN').toUpperCase()}</span>
                          </div>
                        </td>
                        <td style={{ padding: '0.6rem 0.875rem', textAlign: 'right', color: 'var(--theme-text-secondary)', fontSize: '0.79rem' }}>{formatAmount(Number(order.subtotal || 0), order.currency_code)}</td>
                        <td style={{ padding: '0.6rem 0.875rem', textAlign: 'right', fontWeight: 700, color: '#1e293b', fontSize: '0.82rem' }}>{formatAmount(order.total_amount, order.currency_code)}</td>
                        <td style={{ padding: '0.6rem 0.875rem' }}>
                          <div style={{ display: 'flex', gap: '0.28rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {canEditDraft(order) && <button onClick={() => startEditOrder(order)} style={{ padding: '0.28rem 0.52rem', border: 'none', borderRadius: '6px', background: '#fef3c7', color: '#92400e', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600 }}>Edit</button>}
                            {canAddMore(order) && <button onClick={() => startAddOnOrder(order)} style={{ padding: '0.28rem 0.52rem', border: 'none', borderRadius: '6px', background: '#f3e8ff', color: '#6b21a8', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600 }}>Add+</button>}
                            {canSendDraft(order) && <button onClick={() => sendOrderToBar(order.id, { openBillAfter: true, autoPrintKot: true })} style={{ padding: '0.28rem 0.52rem', border: 'none', borderRadius: '6px', background: '#ccfbf1', color: '#134e4a', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600 }}>Send</button>}
                            <button onClick={() => openBillSmart(order.id)} style={{ padding: '0.28rem 0.52rem', border: 'none', borderRadius: '6px', background: '#dbeafe', color: '#1d4ed8', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600 }}>Bill</button>
                            {!isSettledBill(order) && !isVoidedBill(order) && orderWorkflowStatus(order) !== 'DRAFT' && (
                              <button onClick={() => settleOrder(order)} disabled={settlingOrderId === String(order.id)} style={{ padding: '0.28rem 0.52rem', border: 'none', borderRadius: '6px', background: '#dcfce7', color: '#15803d', cursor: settlingOrderId === String(order.id) ? 'wait' : 'pointer', fontSize: '0.7rem', fontWeight: 600 }}>
                                {settlingOrderId === String(order.id) ? '...' : 'Settle'}
                              </button>
                            )}
                            {!isSettledBill(order) && !isVoidedBill(order) && <button onClick={() => voidOrder(order)} style={{ padding: '0.28rem 0.52rem', border: 'none', borderRadius: '6px', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600 }}>Void</button>}
                            <button onClick={() => printOrderById(order.id)} style={{ padding: '0.28rem 0.52rem', border: 'none', borderRadius: '6px', background: '#f0fdf4', color: '#15803d', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600 }}>🖨</button>
                            <button onClick={() => printKotById(order.id)} style={{ padding: '0.28rem 0.52rem', border: 'none', borderRadius: '6px', background: '#f0fdfa', color: '#134e4a', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600 }}>KOT</button>
                            {orderWorkflowStatus(order) === 'DRAFT' && !isSettledBill(order) && !isVoidedBill(order) && <button onClick={() => deleteOrder(order.id)} style={{ padding: '0.28rem 0.52rem', border: 'none', borderRadius: '6px', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600 }}>Del</button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ══════════════ MOBILE LAYOUT (legacy — shown when no step active) ══════════════ */}
      {!step && isMobile && (
        <div style={{ paddingBottom: '68px' }}>
          {/* Action bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0.75rem' }}>
            <span style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>Bar Orders</span>
            <button onClick={() => setStep('service-type')} style={{ padding: '0.4rem 0.75rem', border: 'none', borderRadius: '8px', background: '#1a2151', color: '#fff', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>+ New Order</button>
          </div>

          {/* Tab: Order — Tables + Session + Menu */}
          {mobileTab === 'order' && (
            <div style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>

              {/* Table horizontal scroll */}
              <div style={{ background: '#fff', borderRadius: '12px', padding: '0.875rem', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem', gap: '0.5rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#1e293b', whiteSpace: 'nowrap' }}>Tables</div>
                  <input value={tableSearchTerm} onChange={e => setTableSearchTerm(e.target.value)} placeholder="Search..."
                    style={{ padding: '0.45rem 0.65rem', border: '1px solid var(--theme-border)', borderRadius: '8px', fontSize: '16px', width: '130px', outline: 'none', minHeight: '44px' }} />
                </div>
                {tableCardRow(true)}
              </div>

              {/* Session + checks */}
              {selectedTableId && (
                <div style={{ background: '#fff', borderRadius: '12px', padding: '0.875rem', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b', marginBottom: '0.5rem' }}>
                    {serviceTables.find(t => String(t.id) === String(selectedTableId))?.table_name || 'Table'} — Session
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.55rem' }}>
                    <button type="button" onClick={openSessionForSelectedTable} disabled={!selectedTableId || Boolean(activeTableSession?.id) || sessionBusy}
                      style={{ flex: 1, padding: '0.65rem', border: 'none', borderRadius: '10px', background: (!selectedTableId || Boolean(activeTableSession?.id) || sessionBusy) ? 'var(--theme-border)' : '#10b981', color: (!selectedTableId || Boolean(activeTableSession?.id) || sessionBusy) ? 'var(--theme-text-muted)' : '#fff', cursor: (!selectedTableId || Boolean(activeTableSession?.id) || sessionBusy) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.9rem', minHeight: '48px' }}>
                      Open Session
                    </button>
                    <button type="button" onClick={createNewCheck} disabled={!selectedTableId || !activeTableSession?.id || sessionBusy}
                      style={{ flex: 1, padding: '0.65rem', border: 'none', borderRadius: '10px', background: (!selectedTableId || !activeTableSession?.id || sessionBusy) ? 'var(--theme-border)' : '#3b82f6', color: (!selectedTableId || !activeTableSession?.id || sessionBusy) ? 'var(--theme-text-muted)' : '#fff', cursor: (!selectedTableId || !activeTableSession?.id || sessionBusy) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.9rem', minHeight: '48px' }}>
                      + New Check
                    </button>
                    <button type="button" onClick={closeSessionForSelectedTable} disabled={!activeTableSession?.id || sessionBusy}
                      style={{ flex: 1, padding: '0.65rem', border: 'none', borderRadius: '10px', background: (!activeTableSession?.id || sessionBusy) ? 'var(--theme-border)' : '#ef4444', color: (!activeTableSession?.id || sessionBusy) ? 'var(--theme-text-muted)' : '#fff', cursor: (!activeTableSession?.id || sessionBusy) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.9rem', minHeight: '48px' }}>
                      Clear Session
                    </button>
                  </div>
                  <div style={{ fontSize: '0.79rem', color: 'var(--theme-text-secondary)', marginBottom: '0.55rem' }}>
                    Open Session creates C1. Use New Check only for split customer billing (C2, C3...).
                  </div>
                  {activeTableSession?.id
                    ? sessionChecksBar(true)
                    : <div style={{ fontSize: '0.82rem', color: 'var(--theme-text-muted)', fontStyle: 'italic' }}>No active session</div>
                  }
                </div>
              )}

              {/* Menu grid */}
              <div style={{ background: '#fff', borderRadius: '12px', padding: '0.875rem', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
                {menuGrid(true)}
              </div>
            </div>
          )}

          {/* Tab: Cart */}
          {mobileTab === 'cart' && (
            <div style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {cartPanel(true)}
            </div>
          )}

          {/* Tab: History — order cards */}
          {mobileTab === 'history' && (
            <div style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>Recent Orders</div>
              
              {/* Status Filter Cards */}
              <div style={{ display: 'flex', gap: '0.4rem', overflowX: 'auto', paddingBottom: '0.25rem' }}>
                {[
                  { key: 'ALL', label: 'All', color: 'var(--theme-text-secondary)', bg: 'var(--theme-border-light)' },
                  { key: 'DRAFT', label: 'Draft', color: '#6b21a8', bg: '#f3e8ff' },
                  { key: 'ACTIVE', label: 'Active', color: '#1d4ed8', bg: '#dbeafe' },
                  { key: 'OPEN', label: 'Open', color: '#c2410c', bg: '#fff7ed' },
                  { key: 'SETTLED', label: 'Settled', color: '#15803d', bg: '#dcfce7' },
                  { key: 'VOID', label: 'Void', color: '#b91c1c', bg: '#fee2e2' }
                ].map(status => {
                  const isSelected = statusFilter === status.key;
                  return (
                    <button
                      key={status.key}
                      type="button"
                      onClick={() => setStatusFilter(status.key)}
                      style={{
                        padding: '0.5rem 0.8rem',
                        border: isSelected ? `2px solid ${status.color}` : '1.5px solid var(--theme-border)',
                        borderRadius: '10px',
                        background: isSelected ? status.bg : '#fff',
                        color: isSelected ? status.color : 'var(--theme-text-secondary)',
                        cursor: 'pointer',
                        fontWeight: isSelected ? 700 : 500,
                        fontSize: '0.82rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        flexShrink: 0,
                        minHeight: '44px',
                        boxShadow: isSelected ? '0 2px 4px rgba(0,0,0,0.08)' : 'none'
                      }}
                    >
                      <span>{status.label}</span>
                      <span style={{ padding: '0.1rem 0.4rem', borderRadius: '999px', background: isSelected ? status.color : 'var(--theme-border)', color: isSelected ? '#fff' : 'var(--theme-text-secondary)', fontSize: '0.7rem', fontWeight: 700, minWidth: '20px', textAlign: 'center' }}>
                        {statusCounts[status.key]}
                      </span>
                    </button>
                  );
                })}
              </div>

              {customerSessionFilter && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', padding: '0.7rem 0.85rem', background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: '12px' }}>
                  <span style={{ fontSize: '0.85rem', color: '#1e3a8a' }}>
                    Customer <b style={{ fontFamily: 'monospace' }}>{maskMobile(customerSessionFilter.mobile)}</b> · {filteredOrders.length} order{filteredOrders.length === 1 ? '' : 's'}
                  </span>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button type="button" onClick={() => settleCustomerSession(customerSessionFilter.id, customerSessionFilter.mobile)}
                      disabled={settlingCustomerSid === String(customerSessionFilter.id)}
                      style={{ flex: 1, padding: '0.55rem', border: 'none', borderRadius: '8px', background: '#15803d', color: '#fff', cursor: settlingCustomerSid === String(customerSessionFilter.id) ? 'wait' : 'pointer', fontSize: '0.88rem', fontWeight: 700, minHeight: '44px' }}>
                      {settlingCustomerSid === String(customerSessionFilter.id) ? 'Settling…' : '💳 Settle Customer'}
                    </button>
                    <button type="button" onClick={() => setCustomerSessionFilter(null)}
                      style={{ padding: '0.55rem 0.9rem', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff', color: '#475569', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, minHeight: '44px' }}>
                      Clear
                    </button>
                  </div>
                </div>
              )}
              {!filteredOrders || filteredOrders.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--theme-text-muted)', fontSize: '0.9rem', background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>No orders found</div>
              ) : filteredOrders.map(order => (
                <div key={order.id} style={{ background: '#fff', borderRadius: '12px', padding: '0.875rem', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', border: '1px solid var(--theme-border-light)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.4rem' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.98rem', color: '#1e293b' }}>#{order.id} — {order.table_name || '—'}</div>
                      <div style={{ fontSize: '0.76rem', color: '#0f766e', fontWeight: 700, marginTop: '0.12rem' }}>Check: {String(order.check_with_guest || order.check_codes || '').trim() || '—'}</div>
                      {order.customer_session_id && order.customer_mobile && (
                        <button type="button" onClick={() => setCustomerSessionFilter({ id: order.customer_session_id, mobile: order.customer_mobile })}
                          style={{ marginTop: '0.2rem', border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', padding: '0.18rem 0.5rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'monospace' }}>
                          📱 {maskMobile(order.customer_mobile)}
                        </button>
                      )}
                      <div style={{ fontSize: '0.75rem', color: 'var(--theme-text-secondary)', marginTop: '0.1rem' }}>{fmtDateTime(order.order_date)}</div>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f766e', whiteSpace: 'nowrap', marginLeft: '0.5rem' }}>{formatAmount(order.total_amount, order.currency_code)}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                    <span style={{ padding: '0.2rem 0.55rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 600, background: orderWorkflowStatus(order) === 'DRAFT' ? '#f3e8ff' : '#dbeafe', color: orderWorkflowStatus(order) === 'DRAFT' ? '#6b21a8' : '#1d4ed8' }}>{orderWorkflowStatus(order) || 'DRAFT'}</span>
                    <span style={{ padding: '0.2rem 0.55rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 600, background: isSettledBill(order) ? '#dcfce7' : (isVoidedBill(order) ? '#fee2e2' : '#fff7ed'), color: isSettledBill(order) ? '#15803d' : (isVoidedBill(order) ? '#b91c1c' : '#c2410c') }}>{String(order.bill_status || 'OPEN').toUpperCase()}</span>
                    {order.payment_type && <span style={{ padding: '0.2rem 0.55rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 500, background: 'var(--theme-border-light)', color: 'var(--theme-text-secondary)' }}>{order.payment_type}</span>}
                    {order.linked_settlement_id && <span title={`Combined settlement ref: ${order.linked_settlement_id}`} style={{ padding: '0.2rem 0.55rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>🔗 {String(order.linked_settlement_id).slice(0, 8).toUpperCase()}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    {canEditDraft(order) && <button onClick={() => { startEditOrder(order); setMobileTab('cart'); }} style={{ padding: '0.45rem 0.8rem', border: 'none', borderRadius: '8px', background: '#fef3c7', color: '#92400e', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, minHeight: '40px' }}>Edit</button>}
                    {canAddMore(order) && <button onClick={() => { startAddOnOrder(order); setMobileTab('cart'); }} style={{ padding: '0.45rem 0.8rem', border: 'none', borderRadius: '8px', background: '#f3e8ff', color: '#6b21a8', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, minHeight: '40px' }}>Add+</button>}
                    {canSendDraft(order) && <button onClick={() => sendOrderToBar(order.id, { openBillAfter: true, autoPrintKot: true })} style={{ padding: '0.45rem 0.8rem', border: 'none', borderRadius: '8px', background: '#ccfbf1', color: '#134e4a', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, minHeight: '40px' }}>Send</button>}
                    <button onClick={() => openBillSmart(order.id)} style={{ padding: '0.45rem 0.8rem', border: 'none', borderRadius: '8px', background: '#dbeafe', color: '#1d4ed8', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, minHeight: '40px' }}>Bill</button>
                    {!isSettledBill(order) && !isVoidedBill(order) && orderWorkflowStatus(order) !== 'DRAFT' && (
                      <button onClick={() => settleOrder(order)} disabled={settlingOrderId === String(order.id)} style={{ padding: '0.45rem 0.8rem', border: 'none', borderRadius: '8px', background: '#dcfce7', color: '#15803d', cursor: settlingOrderId === String(order.id) ? 'wait' : 'pointer', fontSize: '0.82rem', fontWeight: 600, minHeight: '40px' }}>
                        {settlingOrderId === String(order.id) ? '...' : 'Settle'}
                      </button>
                    )}
                    {!isSettledBill(order) && !isVoidedBill(order) && <button onClick={() => voidOrder(order)} style={{ padding: '0.45rem 0.8rem', border: 'none', borderRadius: '8px', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, minHeight: '40px' }}>Void</button>}
                    <button onClick={() => printOrderById(order.id)} style={{ padding: '0.45rem 0.8rem', border: 'none', borderRadius: '8px', background: '#f0fdf4', color: '#15803d', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, minHeight: '40px' }}>🖨 Print</button>
                    <button onClick={() => printKotById(order.id)} style={{ padding: '0.45rem 0.8rem', border: 'none', borderRadius: '8px', background: '#f0fdfa', color: '#134e4a', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, minHeight: '40px' }}>KOT</button>
                    {orderWorkflowStatus(order) === 'DRAFT' && !isSettledBill(order) && !isVoidedBill(order) && (
                      <button onClick={() => deleteOrder(order.id)} style={{ padding: '0.45rem 0.8rem', border: 'none', borderRadius: '8px', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, minHeight: '40px' }}>Delete</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Fixed Bottom Tab Bar ── */}
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#1a2151', display: 'flex', borderTop: '1.5px solid rgba(255,255,255,0.1)', zIndex: 950 }}>
            {[
              { key: 'order', label: 'Order', icon: '🍽' },
              { key: 'cart', label: cartItems.length > 0 ? `Cart (${cartItems.length})` : 'Cart', icon: '🛒' },
              { key: 'history', label: 'History', icon: '📋' }
            ].map(tab => (
              <button key={tab.key} type="button" onClick={() => setMobileTab(tab.key)}
                style={{ flex: 1, padding: '0.6rem 0.25rem', border: 'none', background: mobileTab === tab.key ? 'rgba(255,255,255,0.15)' : 'transparent', color: mobileTab === tab.key ? '#fff' : 'rgba(255,255,255,0.5)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.12rem', borderTop: mobileTab === tab.key ? '3px solid #f59e0b' : '3px solid transparent', minHeight: '58px', transition: 'all 0.12s' }}>
                <span style={{ fontSize: '1.3rem', lineHeight: 1 }}>{tab.icon}</span>
                <span style={{ fontSize: '0.7rem', fontWeight: mobileTab === tab.key ? 700 : 400, letterSpacing: '0.02em' }}>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ══════════════ BILL MODAL (Desktop: centered, Mobile: bottom sheet) ══════════════ */}
      {billData && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', zIndex: 1000, padding: isMobile ? 0 : '1rem' }}>
          <div style={{ width: isMobile ? '100%' : 'min(680px, 96vw)', maxHeight: isMobile ? '95vh' : '92vh', overflowY: 'auto', background: '#fff', borderRadius: isMobile ? '20px 20px 0 0' : '16px', boxShadow: '0 25px 50px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column' }}>

            {/* Modal Header */}
            <div style={{ background: '#1a2151', color: '#fff', padding: isMobile ? '1rem' : '1rem 1.25rem', borderRadius: isMobile ? '20px 20px 0 0' : '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {propertyLogoUrl && <img src={propertyLogoUrl} alt="Logo" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 6, background: '#fff', padding: '2px' }} />}
                <div>
                  {billData.is_linked_bill
                    ? <><div style={{ fontWeight: 700, fontSize: isMobile ? '1rem' : '0.95rem' }}>🔗 Combined Bill</div>
                        <div style={{ fontSize: '0.72rem', opacity: 0.7 }}>{(billData.linked_tables || []).join(' + ')}</div></>
                    : <><div style={{ fontWeight: 700, fontSize: isMobile ? '1rem' : '0.95rem' }}>Bill #{billData.id}</div>
                        <div style={{ fontSize: '0.72rem', opacity: 0.7 }}>{billData.table_name || '—'} · {fmtDateTime(billData.order_date)}</div></>
                  }
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                {!billData.is_linked_bill && <span style={{ padding: '0.22rem 0.55rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600, background: orderWorkflowStatus(billData) === 'DRAFT' ? '#a78bfa' : '#60a5fa', color: '#fff' }}>{orderWorkflowStatus(billData) || 'DRAFT'}</span>}
                {!billData.is_linked_bill && <span style={{ padding: '0.22rem 0.55rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600, background: isSettledBill(billData) ? '#86efac' : (isVoidedBill(billData) ? '#fca5a5' : '#fde68a'), color: isSettledBill(billData) ? '#14532d' : (isVoidedBill(billData) ? '#7f1d1d' : '#78350f') }}>{String(billData.bill_status || 'OPEN').toUpperCase()}</span>}
                {billData.is_linked_bill && <span style={{ padding: '0.22rem 0.55rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600, background: '#bfdbfe', color: '#1d4ed8' }}>OPEN</span>}
                {!billData.is_linked_bill && canModifyItems(billData) && (
                  <button onClick={() => setBillEditMode(m => !m)}
                    style={{ padding: '0.3rem 0.65rem', border: 'none', borderRadius: '8px', background: billEditMode ? '#f59e0b' : 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700, minHeight: '32px', whiteSpace: 'nowrap' }}>
                    {billEditMode ? 'Done Editing' : 'Modify Order'}
                  </button>
                )}
                <button onClick={() => { setBillData(null); setBillEditMode(false); }} style={{ padding: '0.35rem 0.65rem', border: 'none', borderRadius: '8px', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', fontSize: '1rem', fontWeight: 700, minWidth: '36px', minHeight: '36px' }}>✕</button>
              </div>
            </div>

            <div style={{ padding: isMobile ? '0.875rem' : '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>

              {/* Meta */}
              {(billData.settled_at || billData.voided_at || billData.void_reason) && (
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {billData.settled_at && <div style={{ fontSize: '0.78rem', color: '#15803d', background: '#f0fdf4', padding: '0.25rem 0.6rem', borderRadius: '6px', border: '1px solid #bbf7d0' }}>Settled: {fmtDateTime(billData.settled_at)}</div>}
                  {billData.voided_at && <div style={{ fontSize: '0.78rem', color: '#b91c1c', background: '#fef2f2', padding: '0.25rem 0.6rem', borderRadius: '6px', border: '1px solid #fecaca' }}>Voided: {fmtDateTime(billData.voided_at)}</div>}
                  {billData.void_reason && <div style={{ fontSize: '0.78rem', color: '#991b1b', background: '#fef2f2', padding: '0.25rem 0.6rem', borderRadius: '6px', border: '1px solid #fecaca' }}>Reason: {billData.void_reason}</div>}
                </div>
              )}

              {/* Billing Details */}
              <div style={{ background: 'var(--theme-bg-light)', border: '1.5px solid var(--theme-border)', borderRadius: '10px', padding: '0.8rem' }}>
                <div style={{ fontWeight: 600, fontSize: '0.75rem', color: 'var(--theme-text-secondary)', marginBottom: '0.45rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Billing Details</div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <select disabled={isSettledBill(billData) || isVoidedBill(billData)} value={billPaymentType} onChange={e => setBillPaymentType(e.target.value)}
                    style={{ flex: 1, minWidth: '150px', padding: '0.55rem 0.6rem', border: '1.5px solid var(--theme-border)', borderRadius: '8px', background: (isSettledBill(billData) || isVoidedBill(billData)) ? 'var(--theme-border-light)' : '#fff', fontSize: '16px', minHeight: '44px' }}>
                    <option value="">— Select Payment Type —</option>
                    <option value="PoS Device 1">PoS Device 1</option>
                    <option value="PoS Device 2">PoS Device 2</option>
                    <option value="Cash">Cash</option>
                  </select>
                  {billData.is_linked_bill
                    ? (billPaymentType
                        ? <div style={{ fontSize: '0.78rem', color: '#15803d', background: '#f0fdf4', padding: '0.35rem 0.65rem', borderRadius: '6px', border: '1px solid #bbf7d0', fontWeight: 600 }}>✓ {billPaymentType} — will apply on settle</div>
                        : <div style={{ fontSize: '0.78rem', color: '#b45309' }}>Select payment type to settle</div>
                      )
                    : <>
                        <button type="button" onClick={() => applyBillingDetails(billData)} disabled={isSettledBill(billData) || isVoidedBill(billData)}
                          style={{ padding: '0.55rem 1rem', border: 'none', borderRadius: '8px', background: (isSettledBill(billData) || isVoidedBill(billData)) ? 'var(--theme-border)' : '#0f766e', color: (isSettledBill(billData) || isVoidedBill(billData)) ? 'var(--theme-text-muted)' : '#fff', cursor: (isSettledBill(billData) || isVoidedBill(billData)) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.88rem', minHeight: '44px' }}>
                          Apply
                        </button>
                        <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>Paid: <b>{billData.payment_type || '—'}</b></div>
                      </>
                  }
                </div>
              </div>

              {/* Order Type + Settlement */}
              <div style={{ background: 'var(--theme-bg-light)', border: '1.5px solid var(--theme-border)', borderRadius: '10px', padding: '0.8rem' }}>
                <div style={{ fontWeight: 600, fontSize: '0.75rem', color: 'var(--theme-text-secondary)', marginBottom: '0.45rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Order Type & Settlement</div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <select value={barSettlement.order_type} onChange={e => setBarSettlement(p => ({ ...p, order_type: e.target.value, settlement_mode: 'DIRECT_PAYMENT', is_external_guest: false, room_number: '', guest_name: '', mobile_last4: '' }))}
                    style={{ flex: 1, minWidth: '145px', padding: '0.55rem 0.6rem', border: '1.5px solid var(--theme-border)', borderRadius: '8px', background: '#fff', fontSize: '16px', minHeight: '44px' }}>
                    <option value="BAR">BAR (Counter)</option>
                    <option value="IN_ROOM_DINING">In-Room Dining</option>
                    <option value="ROOM_SERVICE">Room Service</option>
                  </select>
                  <select value={barSettlement.settlement_mode} onChange={e => setBarSettlement(p => ({ ...p, settlement_mode: e.target.value }))}
                    style={{ flex: 1, minWidth: '145px', padding: '0.55rem 0.6rem', border: '1.5px solid var(--theme-border)', borderRadius: '8px', background: '#fff', fontSize: '16px', minHeight: '44px' }}>
                    <option value="DIRECT_PAYMENT">Direct Payment</option>
                    {['IN_ROOM_DINING', 'ROOM_SERVICE'].includes(barSettlement.order_type) && <option value="PAY_AT_CHECKOUT">Pay at Checkout</option>}
                  </select>
                </div>
                {String(barSettlement.settlement_mode).toUpperCase() === 'PAY_AT_CHECKOUT' && ['IN_ROOM_DINING', 'ROOM_SERVICE'].includes(barSettlement.order_type) && propertyConfig?.module_front_office_enabled !== false && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.88rem', color: '#475569', gridColumn: '1/-1', minHeight: '44px', cursor: 'pointer' }}>
                      <input type="checkbox" style={{ width: '18px', height: '18px' }} checked={barSettlement.is_external_guest === true} onChange={e => { const next = e.target.checked; setBarSettlement(p => ({ ...p, is_external_guest: next, room_number: next ? '' : p.room_number, guest_name: '', mobile_last4: '' })); }} />
                      External guest (room optional)
                    </label>
                    <input type="text" placeholder={barSettlement.is_external_guest ? 'Room (Optional)' : 'Room Number'} value={barSettlement.room_number} onChange={e => setBarSettlement(p => ({ ...p, room_number: e.target.value, guest_name: '', mobile_last4: '' }))} style={{ padding: '0.55rem', border: '1.5px solid var(--theme-border)', borderRadius: '8px', fontSize: '16px', minHeight: '44px' }} />
                    <input type="text" placeholder="Guest Name (auto)" value={barSettlement.guest_name} readOnly style={{ padding: '0.55rem', border: '1.5px solid var(--theme-border)', borderRadius: '8px', background: 'var(--theme-border-light)', fontSize: '16px', minHeight: '44px' }} />
                    <input type="text" placeholder="Mobile Last 4" value={barSettlement.mobile_last4} onChange={e => setBarSettlement(p => ({ ...p, mobile_last4: e.target.value.replace(/\D/g, '').slice(-4) }))} style={{ padding: '0.55rem', border: '1.5px solid var(--theme-border)', borderRadius: '8px', fontSize: '16px', minHeight: '44px' }} />
                    <button type="button" onClick={lookupBarInhouseGuest} style={{ padding: '0.55rem 0.75rem', border: 'none', borderRadius: '8px', background: '#2b6cb0', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem', minHeight: '44px' }}>Lookup Room</button>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.88rem', color: '#475569', minHeight: '44px', cursor: 'pointer' }}>
                      <input type="checkbox" style={{ width: '18px', height: '18px' }} checked={barSettlement.manager_approved === true} onChange={e => setBarSettlement(p => ({ ...p, manager_approved: e.target.checked }))} />
                      Manager approved
                    </label>
                  </div>
                )}
              </div>

              {/* GST Billing Details (optional) */}
              <details style={{ marginTop: '0.5rem', borderRadius: '8px', border: '1.5px solid var(--theme-border)', overflow: 'hidden' }}>
                <summary style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#475569', background: 'var(--theme-bg-light)' }}>GST Billing Details (Optional)</summary>
                <div style={{ padding: '0.5rem 0.75rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.5rem' }}>
                  <input type="text" placeholder="Customer GSTIN" value={barSettlement.customer_gstin} onChange={e => setBarSettlement(p => ({ ...p, customer_gstin: e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 15) }))}
                    style={{ padding: '0.5rem', border: '1.5px solid var(--theme-border)', borderRadius: '8px', fontSize: '14px', fontFamily: 'monospace', minHeight: '40px' }} />
                  <input type="text" placeholder="Company / Trade Name" value={barSettlement.customer_name_bill} onChange={e => setBarSettlement(p => ({ ...p, customer_name_bill: e.target.value }))}
                    style={{ padding: '0.5rem', border: '1.5px solid var(--theme-border)', borderRadius: '8px', fontSize: '14px', minHeight: '40px' }} />
                  <input type="text" placeholder="Customer Address" value={barSettlement.customer_address} onChange={e => setBarSettlement(p => ({ ...p, customer_address: e.target.value }))}
                    style={{ padding: '0.5rem', border: '1.5px solid var(--theme-border)', borderRadius: '8px', fontSize: '14px', minHeight: '40px' }} />
                </div>
                {barSettlement.customer_gstin && barSettlement.customer_gstin.length === 15 && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(barSettlement.customer_gstin) && (
                  <div style={{ padding: '0.25rem 0.75rem 0.5rem', fontSize: '0.75rem', color: '#dc2626' }}>Invalid GSTIN format</div>
                )}
              </details>

              {/* Items table */}
              {billEditMode && <div style={{ padding: '0.5rem 0.75rem', background: '#fef3c7', borderRadius: '8px', border: '1px solid #fcd34d', fontSize: '0.8rem', color: '#92400e', fontWeight: 600 }}>Modify Mode: Change quantities or remove items. Click "Done Editing" when finished.</div>}
              <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1.5px solid var(--theme-border)' }}>
                <table style={{ width: '100%', minWidth: '420px', borderCollapse: 'collapse', background: '#fff' }}>
                  <thead>
                    <tr style={{ background: billEditMode ? '#fef9c3' : 'var(--theme-bg-light)' }}>
                      {[...(billData.is_linked_bill ? ['Table'] : []), 'Item', 'ml', 'Qty', 'Rate', 'Amount', ...(billEditMode ? [''] : [])].map((h, i) => (
                        <th key={h + i} style={{ padding: '0.6rem 0.75rem', textAlign: i > (billData.is_linked_bill ? 1 : 0) ? 'right' : 'left', fontSize: '0.72rem', fontWeight: 600, color: 'var(--theme-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(billData.items || []).map((item, idx) => (
                      <tr key={item.id} style={{ borderTop: '1px solid var(--theme-border-light)', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                        {billData.is_linked_bill && (
                          <td style={{ padding: '0.55rem 0.75rem', fontSize: '0.75rem', color: '#1d4ed8', fontWeight: 600, whiteSpace: 'nowrap' }}>{item.source_table_name || '—'}</td>
                        )}
                        <td style={{ padding: '0.55rem 0.75rem', fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                            {item.menu_item_name || '—'}
                            {item.promo_meta?.promotion_type === 'HAPPY_HOUR' && (
                              <span style={{ fontSize: '0.62rem', fontWeight: 800, background: '#fef3c7', color: '#b45309', border: '1px solid #fcd34d', borderRadius: '4px', padding: '0.05rem 0.3rem', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>HH</span>
                            )}
                            {item.line_type === 'COMP' && (
                              <span style={{ fontSize: '0.62rem', fontWeight: 800, background: '#dcfce7', color: '#15803d', border: '1px solid #86efac', borderRadius: '4px', padding: '0.05rem 0.3rem', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>FREE</span>
                            )}
                          </span>
                          {item.line_type === 'COMP' && item.promo_meta?.rule_name && (
                            <div style={{ fontSize: '0.68rem', color: '#86efac', marginTop: '0.1rem', fontWeight: 400 }}>BOGO · {item.promo_meta.rule_name}</div>
                          )}
                        </td>
                        <td style={{ padding: '0.55rem 0.75rem', textAlign: 'right', color: 'var(--theme-text-secondary)', fontSize: '0.8rem' }}>{item.serving_ml}</td>
                        <td style={{ padding: '0.55rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#1e293b', fontSize: '0.88rem' }}>
                          {billEditMode && item.line_type !== 'COMP' ? (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                              <button disabled={itemBusy === item.id || Number(item.qty_servings) <= 1}
                                onClick={() => updateItemQty(billData.id, item.id, Number(item.qty_servings) - 1)}
                                style={{ width: 26, height: 26, borderRadius: '6px', border: '1.5px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem', color: '#b91c1c', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: itemBusy === item.id ? 0.5 : 1 }}>−</button>
                              <span style={{ minWidth: 22, textAlign: 'center' }}>{itemBusy === item.id ? '…' : item.qty_servings}</span>
                              <button disabled={itemBusy === item.id}
                                onClick={() => updateItemQty(billData.id, item.id, Number(item.qty_servings) + 1)}
                                style={{ width: 26, height: 26, borderRadius: '6px', border: '1.5px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem', color: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: itemBusy === item.id ? 0.5 : 1 }}>+</button>
                            </div>
                          ) : item.qty_servings}
                        </td>
                        <td style={{ padding: '0.55rem 0.75rem', textAlign: 'right', color: 'var(--theme-text-secondary)', fontSize: '0.8rem' }}>
                          {item.promo_meta?.promotion_type === 'HAPPY_HOUR' && Number(item.base_unit_price || 0) > Number(item.unit_price || 0) && (
                            <div style={{ textDecoration: 'line-through', fontSize: '0.72rem', color: '#cbd5e1' }}>{formatAmount(item.base_unit_price, billData.currency_code)}</div>
                          )}
                          {item.line_type === 'COMP'
                            ? <span style={{ color: '#15803d', fontWeight: 700 }}>FREE</span>
                            : formatAmount(item.unit_price, billData.currency_code)
                          }
                        </td>
                        <td style={{ padding: '0.55rem 0.75rem', textAlign: 'right', fontWeight: 700, color: item.line_type === 'COMP' ? '#15803d' : '#0f766e', fontSize: '0.88rem' }}>
                          {item.line_type === 'COMP' ? <span style={{ fontSize: '0.8rem' }}>—</span> : formatAmount(item.line_amount, billData.currency_code)}
                        </td>
                        {billEditMode && (
                          <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center' }}>
                            {item.line_type !== 'COMP' && (
                              <button disabled={itemBusy === item.id}
                                onClick={() => removeItem(billData.id, item.id, item.menu_item_name)}
                                title="Remove item"
                                style={{ padding: '0.2rem 0.5rem', border: 'none', borderRadius: '6px', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem', opacity: itemBusy === item.id ? 0.5 : 1 }}>✕</button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div style={{ background: 'var(--theme-bg-light)', borderRadius: '10px', padding: '0.875rem', border: '1.5px solid var(--theme-border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--theme-text-secondary)', marginBottom: '0.25rem' }}><span>Subtotal</span><span>{formatAmount(billData.subtotal, billData.currency_code)}</span></div>
                {(() => {
                  const hhSavings = (billData.items || []).reduce((sum, it) => {
                    if (it.promo_meta?.promotion_type !== 'HAPPY_HOUR') return sum;
                    const saved = (Number(it.base_unit_price || 0) - Number(it.unit_price || 0)) * Number(it.qty_servings || 0);
                    return sum + (saved > 0 ? saved : 0);
                  }, 0);
                  if (hhSavings <= 0) return null;
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#b45309', background: '#fef9c3', borderRadius: '6px', padding: '0.25rem 0.4rem', marginBottom: '0.18rem', fontWeight: 600 }}>
                      <span>Happy Hour Savings</span>
                      <span>-{formatAmount(hhSavings, billData.currency_code)}</span>
                    </div>
                  );
                })()}
                {(() => {
                  const bogoSavings = (billData.items || []).reduce((sum, it) => {
                    if (it.line_type !== 'COMP') return sum;
                    return sum + Number(it.base_unit_price || 0) * Number(it.qty_servings || 0);
                  }, 0);
                  if (bogoSavings <= 0) return null;
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#15803d', background: '#dcfce7', borderRadius: '6px', padding: '0.25rem 0.4rem', marginBottom: '0.18rem', fontWeight: 600 }}>
                      <span>BOGO Savings (Free Items)</span>
                      <span>-{formatAmount(bogoSavings, billData.currency_code)}</span>
                    </div>
                  );
                })()}
                {(String(billData.tax_regime || '').toUpperCase() === 'GST' || Number(billData.cgst_amount || 0) > 0 || Number(billData.sgst_amount || 0) > 0 || Number(billData.igst_amount || 0) > 0) ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--theme-text-muted)', marginBottom: '0.18rem' }}><span>CGST ({Number(billData.cgst_percent || 0).toFixed(2)}%)</span><span>{formatAmount(billData.cgst_amount, billData.currency_code)}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--theme-text-muted)', marginBottom: '0.18rem' }}><span>SGST ({Number(billData.sgst_percent || 0).toFixed(2)}%)</span><span>{formatAmount(billData.sgst_amount, billData.currency_code)}</span></div>
                    {Number(billData.igst_amount || 0) > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--theme-text-muted)', marginBottom: '0.18rem' }}><span>IGST ({Number(billData.igst_percent || 0).toFixed(2)}%)</span><span>{formatAmount(billData.igst_amount, billData.currency_code)}</span></div>}
                  </>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--theme-text-muted)', marginBottom: '0.18rem' }}><span>Tax ({Number(billData.gst_percent || 0).toFixed(2)}%)</span><span>{formatAmount(billData.gst_amount, billData.currency_code)}</span></div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', marginTop: '0.4rem', paddingTop: '0.4rem', borderTop: '2px solid var(--theme-border)' }}>
                  <span>Total</span><span style={{ color: '#0f766e' }}>{formatAmount(billData.total_amount, billData.currency_code)}</span>
                </div>
              </div>

              {/* Modal actions */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: isMobile ? 'stretch' : 'flex-end', paddingBottom: '0.25rem' }}>
                <button onClick={() => printBill(billData)} style={{ flex: isMobile ? 1 : 'none', padding: '0.6rem 1rem', border: 'none', borderRadius: '8px', background: '#dcfce7', color: '#15803d', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem', minHeight: '44px', whiteSpace: 'nowrap' }}>🖨 Print</button>
                {!billData.is_linked_bill && <button onClick={() => printKotSlip(billData)} style={{ flex: isMobile ? 1 : 'none', padding: '0.6rem 1rem', border: 'none', borderRadius: '8px', background: '#ccfbf1', color: '#134e4a', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem', minHeight: '44px', whiteSpace: 'nowrap' }}>KOT</button>}
                {billData.is_linked_bill
                  ? (
                    <button onClick={() => settleLinkedBill(billData.primary_session_id, billData.linked_tables)} disabled={linkBusy}
                      style={{ flex: isMobile ? '0 0 100%' : 'none', padding: '0.6rem 1rem', border: 'none', borderRadius: '8px', background: linkBusy ? 'var(--theme-border)' : '#10b981', color: linkBusy ? 'var(--theme-text-muted)' : '#fff', cursor: linkBusy ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: '0.95rem', minHeight: '48px', whiteSpace: 'nowrap' }}>
                      {linkBusy ? 'Settling...' : `✓ Settle Combined Bill (${(billData.linked_tables || []).join(' + ')})`}
                    </button>
                  ) : (
                    <>
                      {!isSettledBill(billData) && !isVoidedBill(billData) && orderWorkflowStatus(billData) !== 'DRAFT' && (
                        <button onClick={() => settleOrder(billData)} disabled={!canSettleOrder(billData) || settlingOrderId === String(billData.id)}
                          style={{ flex: isMobile ? '0 0 100%' : 'none', padding: '0.6rem 1rem', border: 'none', borderRadius: '8px', background: (!canSettleOrder(billData) || settlingOrderId === String(billData.id)) ? 'var(--theme-border)' : '#10b981', color: (!canSettleOrder(billData) || settlingOrderId === String(billData.id)) ? 'var(--theme-text-muted)' : '#fff', cursor: (!canSettleOrder(billData) || settlingOrderId === String(billData.id)) ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: '0.95rem', minHeight: '48px', whiteSpace: 'nowrap' }}>
                          {settlingOrderId === String(billData.id) ? 'Settling...' : (!canSettleOrder(billData) ? 'Set Billing First' : '✓ Settle Bill')}
                        </button>
                      )}
                      {!isSettledBill(billData) && !isVoidedBill(billData) && (
                        <button onClick={() => voidOrder(billData)} style={{ flex: isMobile ? 1 : 'none', padding: '0.6rem 1rem', border: 'none', borderRadius: '8px', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem', minHeight: '44px', whiteSpace: 'nowrap' }}>Void</button>
                      )}
                    </>
                  )
                }
                <button onClick={() => setBillData(null)} style={{ flex: isMobile ? 1 : 'none', padding: '0.6rem 1rem', border: '1.5px solid var(--theme-border)', borderRadius: '8px', background: '#fff', color: 'var(--theme-text-secondary)', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem', minHeight: '44px', whiteSpace: 'nowrap' }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Orders;
